import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { 
  Database, Search, Plus, Edit2, Trash2, Upload, Download, 
  Gamepad2, Trophy, ChevronRight, Loader2, ExternalLink,
  ChevronLeft, ChevronsLeft, ChevronsRight, Image as ImageIcon,
  Save, X, Check, Package, Calendar, Settings, Copy, Tag, MoveRight,
  Filter, Bug, FileJson, Library, MoreVertical, AlertCircle,
  RefreshCw, ArrowRight, Lightbulb, Target, Eye, EyeOff, Layers,
  Users, UserPlus, Shield, ShieldCheck, UserCog, FileText, Images, Server
} from "lucide-react";
import { CopyTagsDialog } from "@/components/MasterDatabase/CopyTagsDialog";
import AutoLinkVersionsDialog from "@/components/MasterDatabase/AutoLinkVersionsDialog";
import BulkRelateDialog from "@/components/MasterDatabase/BulkRelateDialog";
import { BulkEditDialog } from "@/components/MasterDatabase/BulkEditDialog";
import { MasterDlcList } from "@/components/MasterDatabase/MasterDlcList";
import AchievementImage from "@/components/AchievementImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import ImageUpload, { ImageUploadButton } from "@/components/ImageUpload";
import SystemBarOverlay, { getSystemBarOptions } from "@/components/SystemBarOverlay";
import {
  XboxLookupDialog,
  SteamLookupDialog,
  PSNLookupDialog,
  BugReportsDialog,
  KnownIssuesDialog,
  GameCoverArtDialog,
  DlcCoverArtDialog,
  DBoxUpdateDialog,
  EditGameDialog,
  EditAchievementDialog,
  EditDlcDialog,
  BulkEditAchievementDialog,
  CreateGameDialog,
  CreateAchievementDialog,
  CreateDlcDialog,
  TagManagerDialog,
  BulkAchievementTagsDialog,
} from "@/components/MasterDatabase";
import { AchievementFactsManager, PendingFactBadge } from "@/components/admin/FactsManager";
import PlatformCoverageDialog from "@/components/PlatformCoverageDialog";
import CustomPlatformRequestsDialog from "@/components/CustomPlatformRequestsDialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

const API = `${getApiUrl()}/api`;

// Helper to format YYYY-MM-DD to MM/DD/YYYY for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }
  return dateStr;
};

// Helper to parse MM/DD/YYYY to YYYY-MM-DD for storage
const parseDateFromDisplay = (displayStr) => {
  if (!displayStr) return "";
  const match = displayStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[1]}-${match[2]}`;
  }
  return "";
};

// Helper for auto-masking date input (MM/DD/YYYY)
const handleDateInputMask = (inputValue, previousValue) => {
  // Remove all non-digits
  let digits = inputValue.replace(/\D/g, "");
  
  // Limit to 8 digits (MMDDYYYY)
  digits = digits.slice(0, 8);
  
  // Build the formatted string
  let formatted = "";
  if (digits.length > 0) {
    formatted = digits.slice(0, 2);
  }
  if (digits.length > 2) {
    formatted += "/" + digits.slice(2, 4);
  }
  if (digits.length > 4) {
    formatted += "/" + digits.slice(4, 8);
  }
  
  return formatted;
};

export default function MasterDatabase() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Games list state
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get("search") || "");
  const [platformFilter, setPlatformFilter] = useState(searchParams.get("platform") || "all");
  const [systemFilter, setSystemFilter] = useState(searchParams.get("system") || "all");
  const [releaseYearFilter, setReleaseYearFilter] = useState(searchParams.get("year") || "all");
  const [regionFilter, setRegionFilter] = useState(searchParams.get("region") || "all");
  const [missingFilter, setMissingFilter] = useState(searchParams.get("missing") || "all");
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sortOrder") || "asc");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [totalGames, setTotalGames] = useState(0);
  const gamesPerPage = 50;
  
  // Debounce timer ref
  const searchDebounceRef = useRef(null);
  
  // Dynamic platforms/systems from config
  const [platforms, setPlatforms] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [unifiedTags, setUnifiedTags] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [genres, setGenres] = useState([]);
  const [regions, setRegions] = useState([]);
  // Admin-only tag groups (achievement & unified) — used to organize the
  // long tag lists inside MDB input dialogs. Created in MasterDatabaseTags.
  const [achievementTagGroups, setAchievementTagGroups] = useState([]);
  const [unifiedTagGroups, setUnifiedTagGroups] = useState([]);
  
  // Tag management state
  const [showTagManager, setShowTagManager] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#64748B");
  const [newTagDescription, setNewTagDescription] = useState("");
  
  // Bulk achievement tag editing
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
  
  // Fact reviews state
  const [gamesWithPendingFacts, setGamesWithPendingFacts] = useState([]);
  const [pendingFactsForGame, setPendingFactsForGame] = useState({});
  
  // Pending submissions state
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [pendingOrganizers, setPendingOrganizers] = useState([]);
  const [pendingGamePlanners, setPendingGamePlanners] = useState([]);
  
  // Missing field filter options
  const missingFieldOptions = [
    { value: "all", label: "All Games" },
    { value: "published", label: "✅ Published Only" },
    { value: "unpublished", label: "🚫 Unpublished Only" },
    { value: "needsReview", label: "📋 Needs Review (Scraped)" },
    { value: "noReview", label: "✨ Not Flagged for Review" },
    { value: "boxArt", label: "Missing Box Art" },
    { value: "releaseDate", label: "Missing Release Date" },
    { value: "publisher", label: "Missing Publisher" },
    { value: "developer", label: "Missing Developer" },
    { value: "systems", label: "Missing Systems" },
    { value: "availability", label: "Missing Availability" },
    { value: "achievements", label: "No Achievements" },
    { value: "any", label: "Any Missing Field" },
  ];

  // Review type categories — keep in sync with backend ALLOWED_REVIEW_TYPES.
  const REVIEW_TYPE_OPTIONS = [
    { value: "tags",    label: "Achievement Tag Review" },
    { value: "secrets", label: "Secrets Review" },
    { value: "dlc",     label: "DLC Review" },
    { value: "info",    label: "Information Review" },
    { value: "other",   label: "Other / General Review" },
  ];
  
  // Sort options
  const sortOptions = [
    { value: "name", label: "Name" },
    { value: "releaseDate", label: "Release Date" },
    { value: "totalAchievements", label: "Achievements" },
    { value: "createdAt", label: "Date Added" },
  ];
  
  // Scraper state
  const [scraperStatus, setScraperStatus] = useState(null);
  const [scraperLoading, setScraperLoading] = useState(false);
  
  // Selected game state
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedGameAchievements, setSelectedGameAchievements] = useState([]);

  // Multi-select mode for bulk actions on the games list (mark for review,
  // publish/unpublish). When `bulkMode` is on, clicking a row toggles its
  // inclusion in `bulkSelectedIds` instead of opening the game's detail view.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  // Cache of selected game OBJECTS keyed by id, accumulated across pages. The
  // grid only holds the current page's games, so bulk tools that need the full
  // game object (e.g. the drag-and-drop Relate dialog, union pre-fills) read
  // from here to keep selections made on other pages. `bulkSelectedIds` stays
  // the source of truth for which ids are selected; stale cache entries are
  // harmless since callers always filter by the id set.
  const selectedGamesCacheRef = useRef(new Map());
  const getSelectedGameObjects = () =>
    Array.from(bulkSelectedIds)
      .map((id) => selectedGamesCacheRef.current.get(id))
      .filter(Boolean);
  // In-app confirmation dialog state. Replaces window.confirm() which some
  // browsers (and security extensions) silently suppress so a click on a
  // bulk-action button would appear to do nothing.
  const [bulkConfirm, setBulkConfirm] = useState(null); // { title, description, confirmLabel, variant, onConfirm }
  const [bulkRunning, setBulkRunning] = useState(false);

  // Bulk "Set Review Types" dialog state (set-exact semantics).
  const [reviewTypesDialogOpen, setReviewTypesDialogOpen] = useState(false);
  const [reviewTypesDialogValues, setReviewTypesDialogValues] = useState(new Set());

  // Bulk "Set Platforms" / "Set Systems" dialog state — same set-exact semantics.
  const [platformsDialogOpen, setPlatformsDialogOpen] = useState(false);
  const [platformsDialogValues, setPlatformsDialogValues] = useState(new Set());
  const [systemsDialogOpen, setSystemsDialogOpen] = useState(false);
  const [systemsDialogValues, setSystemsDialogValues] = useState(new Set());
  // Bulk Edit dialog (mega dialog with per-field apply toggles).
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);

  // Review-type multi-select filter on the games list.
  const [reviewTypesFilter, setReviewTypesFilter] = useState([]);

  // Consistent sort for master database achievements: base game first, then by DLC name, then name
  const sortMasterAchievements = (achs) => {
    return [...achs].sort((a, b) => {
      const aIsDlc = a.dlcId || a.dlcName ? 1 : 0;
      const bIsDlc = b.dlcId || b.dlcName ? 1 : 0;
      if (aIsDlc !== bIsDlc) return aIsDlc - bIsDlc;
      const dlcCmp = (a.dlcName || "").toLowerCase().localeCompare((b.dlcName || "").toLowerCase());
      if (dlcCmp !== 0) return dlcCmp;
      return (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
    });
  };
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  
  // Image upload state
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadImagesDialogOpen, setUploadImagesDialogOpen] = useState(false);
  const [uploadOptions, setUploadOptions] = useState({
    boxArt: false,
    dlcBoxArt: false,
    achievements: false
  });
  
  // Dialog states
  const [createGameDialogOpen, setCreateGameDialogOpen] = useState(false);
  const [addFromRequestId, setAddFromRequestId] = useState(null);
  const [similarGameMatches, setSimilarGameMatches] = useState([]);
  const [editGameDialogOpen, setEditGameDialogOpen] = useState(false);
  const [createAchievementDialogOpen, setCreateAchievementDialogOpen] = useState(false);
  const [editAchievementDialogOpen, setEditAchievementDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFromLibraryDialogOpen, setImportFromLibraryDialogOpen] = useState(false);
  const [createDlcDialogOpen, setCreateDlcDialogOpen] = useState(false);
  const [editDlcDialogOpen, setEditDlcDialogOpen] = useState(false);
  
  // Fact management states
  const [factsDialogOpen, setFactsDialogOpen] = useState(false);
  const [factsAchievement, setFactsAchievement] = useState(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [addMemberLevel, setAddMemberLevel] = useState("viewer");
  const [addingMember, setAddingMember] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  
  // Platform ID lookup states
  const [xboxLookupLoading, setXboxLookupLoading] = useState(false);
  const [xboxLookupResults, setXboxLookupResults] = useState([]);
  const [xboxLookupDialogOpen, setXboxLookupDialogOpen] = useState(false);
  const [xboxLookupTarget, setXboxLookupTarget] = useState(null); // 'new' or 'edit'
  
  const [steamLookupLoading, setSteamLookupLoading] = useState(false);
  const [steamLookupResults, setSteamLookupResults] = useState([]);
  const [steamLookupDialogOpen, setSteamLookupDialogOpen] = useState(false);
  const [steamLookupTarget, setSteamLookupTarget] = useState(null);
  
  const [psnLookupLoading, setPsnLookupLoading] = useState(false);
  const [psnLookupResults, setPsnLookupResults] = useState([]);
  const [psnLookupDialogOpen, setPsnLookupDialogOpen] = useState(false);
  const [psnLookupTarget, setPsnLookupTarget] = useState(null);
  
  // Exophase Import
  const [exophaseDialogOpen, setExophaseDialogOpen] = useState(false);
  // Copy Tags Dialog
  const [copyTagsDialogOpen, setCopyTagsDialogOpen] = useState(false);
  // Copy Game dialog — asks whether to make the copy a linked version
  const [copyDialog, setCopyDialog] = useState({ open: false, gameId: null, gameName: "" });
  // Auto-Link Duplicate Versions tool
  const [autoLinkOpen, setAutoLinkOpen] = useState(false);
  // Bulk relate (remaster/remake/alternate/collection) dialog
  const [bulkRelateOpen, setBulkRelateOpen] = useState(false);
  const [exophaseUrl, setExophaseUrl] = useState("");
  const [exophaseHtml, setExophaseHtml] = useState("");
  const [dboxApplyExophaseScores, setDboxApplyExophaseScores] = useState(false);
  const [exophaseInputMode, setExophaseInputMode] = useState("url"); // "url" or "html"
  const [exophaseLoading, setExophaseLoading] = useState(false);
  const [exophaseAchievements, setExophaseAchievements] = useState([]);
  const [exophaseImportProgress, setExophaseImportProgress] = useState(0);
  const [exophaseImporting, setExophaseImporting] = useState(false);
  const [exophaseImagesOnly, setExophaseImagesOnly] = useState(false); // NEW: Update images only mode
  const [exophaseAddNewOnly, setExophaseAddNewOnly] = useState(false); // Add new achievements only (skip existing by name)
  const [exophaseApplyScores, setExophaseApplyScores] = useState(false); // Also update XP/Gamerscore from parsed Exophase data
  
  // Bulk Achievement Move
  const [achBulkSelectMode, setAchBulkSelectMode] = useState(false);
  const [selectedAchievementIds, setSelectedAchievementIds] = useState([]);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState("none"); // "none" for base game, or dlcId
  const [bulkEditAchDialogOpen, setBulkEditAchDialogOpen] = useState(false);
  const [bulkEditAchQueue, setBulkEditAchQueue] = useState([]);
  const [bulkEditAchCurrent, setBulkEditAchCurrent] = useState(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkEditAchIndex, setBulkEditAchIndex] = useState(0);
  
  // Bug Reports Admin
  const [bugReportsDialogOpen, setBugReportsDialogOpen] = useState(false);
  const [bugReports, setBugReports] = useState([]);
  const [bugReportsLoading, setBugReportsLoading] = useState(false);
  const [knownIssuesDialogOpen, setKnownIssuesDialogOpen] = useState(false);
  const [knownIssues, setKnownIssues] = useState([]);
  const [knownIssuesLoading, setKnownIssuesLoading] = useState(false);
  const [editingBugReport, setEditingBugReport] = useState(null);
  const [editingKnownIssue, setEditingKnownIssue] = useState(null);
  const [newKnownIssue, setNewKnownIssue] = useState({
    title: "",
    description: "",
    status: "known",
    workaround: "",
    affectedAreas: [],
  });
  
  // Genre remapping state
  const [genreRemapDialogOpen, setGenreRemapDialogOpen] = useState(false);
  const [genreToRemap, setGenreToRemap] = useState(null);
  const [remapTargetGenre, setRemapTargetGenre] = useState("");
  
  // DBox Update state
  const [dboxUpdateDialogOpen, setDboxUpdateDialogOpen] = useState(false);
  const [dboxPreviewLoading, setDboxPreviewLoading] = useState(false);
  const [dboxPreviewData, setDboxPreviewData] = useState(null);
  const [dboxSelectedFields, setDboxSelectedFields] = useState([]);
  const [dboxApplyAchievements, setDboxApplyAchievements] = useState(false);
  const [dboxUploadToCloudinary, setDboxUploadToCloudinary] = useState(true);
  const [dboxUpdateLoading, setDboxUpdateLoading] = useState(false);
  const [dboxExophaseHtml, setDboxExophaseHtml] = useState("");
  const [dboxCustomBoxArtUrl, setDboxCustomBoxArtUrl] = useState("");
  
  // Unified Cover Art Lookup state (IGDB + SteamGridDB)
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [coverSearchQuery, setCoverSearchQuery] = useState("");
  const [coverResults, setCoverResults] = useState({ igdb: [], steamgriddb: [], combined: [] });
  const [coverLoading, setCoverLoading] = useState(false);
  const [selectedCover, setSelectedCover] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverSourceFilter, setCoverSourceFilter] = useState("all"); // "all", "igdb", "steamgriddb"
  
  // DLC Cover Art Lookup state
  const [dlcCoverDialogOpen, setDlcCoverDialogOpen] = useState(false);
  const [dlcCoverSearchQuery, setDlcCoverSearchQuery] = useState("");
  const [dlcCoverResults, setDlcCoverResults] = useState({ igdb: [], steamgriddb: [], combined: [] });
  const [dlcCoverLoading, setDlcCoverLoading] = useState(false);
  const [selectedDlcCover, setSelectedDlcCover] = useState(null);
  const [dlcCoverUploading, setDlcCoverUploading] = useState(false);
  const [dlcCoverSourceFilter, setDlcCoverSourceFilter] = useState("all");
  const [dlcForCoverSearch, setDlcForCoverSearch] = useState(null); // The DLC we're searching covers for
  
  // Filters popover state
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Platform Diagnostics state
  const [platformDiagnosticsOpen, setPlatformDiagnosticsOpen] = useState(false);
  const [platformDiagnostics, setPlatformDiagnostics] = useState(null);
  const [platformDiagnosticsLoading, setPlatformDiagnosticsLoading] = useState(false);

  // Platform Coverage state
  const [platformCoverageOpen, setPlatformCoverageOpen] = useState(false);
  const [gameRequestCount, setGameRequestCount] = useState(0);
  // Custom platform/system requests state
  const [psRequestsOpen, setPsRequestsOpen] = useState(false);
  const [psRequestCount, setPsRequestCount] = useState(0);

  // Fetch game request count on mount
  useEffect(() => {
    const fetchRequestCount = async () => {
      try {
        const { data } = await axios.get(`${API}/game-requests/counts`);
        setGameRequestCount(data.total || 0);
      } catch {
        // Silently fail - user may not have admin access
      }
    };
    fetchRequestCount();
  }, [platformCoverageOpen]); // Refresh when dialog closes

  // Fetch custom platform/system request count
  useEffect(() => {
    const fetchPsCount = async () => {
      try {
        const { data } = await axios.get(`${API}/platform-system-requests/counts`);
        setPsRequestCount(data.total || 0);
      } catch {
        // Silently fail - user may not have admin access
      }
    };
    fetchPsCount();
  }, [psRequestsOpen]);
  const [normalizingPlatforms, setNormalizingPlatforms] = useState(false);
  
  // Form states
  const [newGame, setNewGame] = useState({
    name: "",
    boxArtUrl: "",
    releaseDate: "",
    publisher: "",
    developer: "",
    genres: [],
    completionEstimate: "",
    platforms: [],
    systems: [],
    region: "none",
    availableDigital: false,
    availablePhysical: false,
    contentTags: [],
    xboxTitleId: "",
    xboxBingId: "",
    steamAppId: "",
    npCommunicationId: "",
  });
  const [editingGame, setEditingGame] = useState(null);
  const [newAchievement, setNewAchievement] = useState({
    name: "",
    description: "",
    imageUrl: "",
    score: 0,
    trophyType: null,
    isSecret: false,
    isPerCharacter: false,
    isMissable: false,
    isOnline: false,
    isUnobtainable: false,
    requiresExternalContent: false,
    dlcId: null,
    dlcName: "",
  });
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [newDlc, setNewDlc] = useState({
    name: "",
    boxArtUrl: "",
    releaseDate: "",
    completionEstimate: "",
    region: "none",
  });
  const [editingDlc, setEditingDlc] = useState(null);
  
  // Region options
  const regionOptions = [
    { value: "none", label: "No Region" },
    ...regions.map((r) => ({ value: r.code, label: r.name })),
  ];
  
  // Build system options from dynamic platforms
  const getSystemOptions = () => {
    const options = {};
    platforms.forEach(p => {
      options[p.slug] = (p.systems || []).map(s => ({ value: s.slug, label: s.name, id: s.id }));
    });
    return options;
  };
  const systemOptions = getSystemOptions();
  
  // Helper to get system display name from slug
  const getSystemDisplayName = (systemSlug) => {
    for (const platform of platforms) {
      for (const sys of (platform.systems || [])) {
        if (sys.slug === systemSlug || sys.slug?.toLowerCase() === systemSlug?.toLowerCase()) {
          return sys.name;
        }
      }
    }
    // Fallback: format slug nicely if not found
    return systemSlug?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || systemSlug;
  };
  
  // Get all systems as flat list for filter dropdown
  const getAllSystems = () => {
    const systems = [];
    platforms.forEach(p => {
      (p.systems || []).forEach(s => {
        systems.push({ value: s.slug, label: `${s.name} (${p.name})`, platformSlug: p.slug });
      });
    });
    return systems;
  };
  
  // Get content tags for selected systems
  const getAvailableContentTags = (selectedSystems) => {
    const systemSlugs = new Set(selectedSystems || []);
    const systemIds = new Set();
    
    platforms.forEach(p => {
      (p.systems || []).forEach(s => {
        if (systemSlugs.has(s.slug)) {
          systemIds.add(s.id);
        }
      });
    });
    
    return contentTags.filter(t => systemIds.has(t.systemId));
  };
  
  // Import from library state
  const [libraryGames, setLibraryGames] = useState([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Export/Import database state
  const [importFile, setImportFile] = useState(null);
  const [importMergeStrategy, setImportMergeStrategy] = useState("none");
  const [importingDb, setImportingDb] = useState(false);
  
  // Pending counts for badges
  const [pendingCounts, setPendingCounts] = useState({ facts: 0, challenges: 0, organizers: 0 });
  const [tutorialUnread, setTutorialUnread] = useState(0);
  const [bugTotalCount, setBugTotalCount] = useState(0);
  
  // Fetch platforms, content tags, and genres
  const fetchConfig = async () => {
    try {
      const [platformsRes, tagsRes, unifiedTagsRes, predefinedTagsRes, genresRes, pendingFactsRes, pendingChallengesRes, pendingOrganizersRes, pendingGamePlannersRes, achGroupsRes, unifiedGroupsRes] = await Promise.all([
        axios.get(`${API}/master/platforms`),
        axios.get(`${API}/master/content-tags`),
        axios.get(`${API}/master/unified-tags`),
        axios.get(`${API}/master/predefined-tags`),
        axios.get(`${API}/master/genres`),
        axios.get(`${API}/achievement-facts/admin/games-with-pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/challenges/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/organizers/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/game-planners/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/master-tags/groups`).catch(() => ({ data: [] })),
        axios.get(`${API}/master-tags/unified-groups`).catch(() => ({ data: [] })),
      ]);
      setPlatforms(platformsRes.data);
      setContentTags(tagsRes.data);
      setUnifiedTags(unifiedTagsRes.data);
      setPredefinedTags(predefinedTagsRes.data);
      setGenres(genresRes.data);
      axios.get(`${API}/regions`).then((r) => setRegions(r.data || [])).catch(() => {});
      setAchievementTagGroups(achGroupsRes.data || []);
      setUnifiedTagGroups(unifiedGroupsRes.data || []);
      setGamesWithPendingFacts(pendingFactsRes.data || []);
      
      // Store pending challenges, organizers, and game planners
      setPendingChallenges(pendingChallengesRes.data || []);
      setPendingOrganizers(pendingOrganizersRes.data || []);
      setPendingGamePlanners(pendingGamePlannersRes.data || []);
      
      // Update pending counts
      setPendingCounts({
        facts: (pendingFactsRes.data || []).length,
        challenges: (pendingChallengesRes.data || []).length,
        organizers: (pendingOrganizersRes.data || []).length,
        planners: (pendingGamePlannersRes.data || []).length
      });

      // Tutorial feedback unread badge (MDB → Bugs)
      try {
        const unreadRes = await axios.get(`${API}/bug-reports/tutorial-unread-count`);
        setTutorialUnread(unreadRes.data?.count || 0);
      } catch (e) { /* non-blocking */ }

      // Total bug reports count (MDB → Bugs button badge)
      try {
        const countRes = await axios.get(`${API}/bug-reports/count`);
        setBugTotalCount(countRes.data?.count || 0);
      } catch (e) { /* non-blocking */ }
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  // Fetch master games
  const fetchGames = async () => {
    setLoading(true);
    try {
      // Special handling for pendingFacts filter - use client-side filtering
      if (missingFilter === "pendingFacts") {
        // Fetch games that have pending fact submissions
        const pendingRes = await axios.get(`${API}/achievement-facts/admin/games-with-pending`).catch(() => ({ data: [] }));
        const pendingGames = pendingRes.data || [];
        setGamesWithPendingFacts(pendingGames);
        
        if (pendingGames.length === 0) {
          setGames([]);
          setTotalGames(0);
          setLoading(false);
          return;
        }
        
        // Fetch each game's details
        const gameIds = pendingGames.map(g => g.masterGameId);
        const gamePromises = gameIds.slice(0, 50).map(id => 
          axios.get(`${API}/master/games/${id}`).catch(() => null)
        );
        const gameResponses = await Promise.all(gamePromises);
        const validGames = gameResponses
          .filter(r => r?.data)
          .map(r => r.data);
        
        setGames(validGames);
        setTotalGames(validGames.length);
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams();
      if (debouncedSearchQuery) params.append("search", debouncedSearchQuery);
      if (platformFilter !== "all") params.append("platform", platformFilter);
      if (systemFilter !== "all") params.append("system", systemFilter);
      if (releaseYearFilter !== "all") params.append("releaseYear", releaseYearFilter);
      if (regionFilter !== "all") params.append("region", regionFilter);
      if (missingFilter !== "all") params.append("missing", missingFilter);
      // Multi-select review-type filter — append each type so the backend
      // sees `?review_types=tags&review_types=secrets`.
      for (const t of reviewTypesFilter) params.append("review_types", t);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      params.append("limit", gamesPerPage);
      params.append("skip", (currentPage - 1) * gamesPerPage);
      
      const response = await axios.get(`${API}/master/games?${params}`);
      setGames(response.data.games);
      setTotalGames(response.data.total);
    } catch (error) {
      console.error("Failed to fetch master games:", error);
      if (error.response?.status === 429) {
        toast.error("Too many requests. Please wait a moment before searching again.");
      } else {
        toast.error("Failed to load master database");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch game details and achievements
  const fetchGameDetails = async (gameId) => {
    setLoadingAchievements(true);
    try {
      const [gameRes, pendingFactsRes] = await Promise.all([
        axios.get(`${API}/master/games/${gameId}`),
        axios.get(`${API}/achievement-facts/admin/game/${gameId}/pending-achievements`).catch(() => ({ data: {} })),
      ]);
      setSelectedGame(gameRes.data);
      setSelectedGameAchievements(gameRes.data.achievements || []);
      setPendingFactsForGame(pendingFactsRes.data || {});
    } catch (error) {
      console.error("Failed to fetch game details:", error);
      toast.error("Failed to load game details");
    } finally {
      setLoadingAchievements(false);
    }
  };

  // ============ MEMBER MANAGEMENT ============
  const fetchMembers = async () => {
    setMembersLoading(true);
    try {
      const response = await axios.get(`${API}/master/members`);
      setMembers(response.data);
    } catch (error) {
      console.error("Failed to fetch members:", error);
      toast.error("Failed to load members");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!addMemberEmail.trim()) {
      toast.error("Email is required");
      return;
    }
    setAddingMember(true);
    try {
      await axios.post(`${API}/master/permissions`, {
        email: addMemberEmail.trim(),
        level: addMemberLevel,
      });
      toast.success("Member added successfully");
      setAddMemberEmail("");
      setAddMemberLevel("viewer");
      fetchMembers();
    } catch (error) {
      console.error("Failed to add member:", error);
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateMemberLevel = async (userId, newLevel) => {
    try {
      await axios.put(`${API}/master/permissions/${userId}`, { level: newLevel });
      toast.success("Permission updated");
      fetchMembers();
    } catch (error) {
      console.error("Failed to update permission:", error);
      toast.error("Failed to update permission");
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm("Are you sure you want to remove this member's access?")) return;
    try {
      await axios.delete(`${API}/master/permissions/${userId}`);
      toast.success("Member removed");
      fetchMembers();
    } catch (error) {
      console.error("Failed to remove member:", error);
      toast.error("Failed to remove member");
    }
  };

  // Search for users to grant permissions
  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchedUsers([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const response = await axios.get(`${API}/master/users/search`, {
        params: { q: query, limit: 20 }
      });
      setSearchedUsers(response.data);
    } catch (error) {
      console.error("Failed to search users:", error);
      toast.error("Failed to search users");
    } finally {
      setSearchingUsers(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchUsers(userSearchQuery);
      } else {
        setSearchedUsers([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // Grant permission to a user from search
  const handleGrantPermission = async (user, level) => {
    try {
      await axios.post(`${API}/master/permissions`, {
        email: user.email,
        level: level,
      });
      toast.success(`Granted ${level} access to ${user.email}`);
      setUserSearchQuery("");
      setSearchedUsers([]);
      setShowUserSearch(false);
      fetchMembers();
    } catch (error) {
      console.error("Failed to grant permission:", error);
      toast.error(error.response?.data?.detail || "Failed to grant permission");
    }
  };

  // Create game
  const handleCreateGame = async () => {
    if (!newGame.name.trim()) {
      toast.error("Game name is required");
      return;
    }
    try {
      const payload = {
        ...newGame,
        genres: newGame.genres || [],
        region: newGame.region === "none" ? null : newGame.region,
        contentTags: newGame.contentTags || [],
        xboxTitleId: newGame.xboxTitleId || null,
        xboxBingId: newGame.xboxBingId || null,
        steamAppId: newGame.steamAppId ? parseInt(newGame.steamAppId) : null,
        npCommunicationId: newGame.npCommunicationId || null,
      };
      delete payload._dateDisplay;
      const response = await axios.post(`${API}/master/games`, payload);
      toast.success(`Created "${response.data.name}"`);
      // If this game was created from a user coverage request, auto-mark it added.
      if (addFromRequestId) {
        try {
          await axios.put(`${API}/game-requests/${addFromRequestId}/status`, { status: "added" });
        } catch (e) { /* non-fatal */ }
        setAddFromRequestId(null);
      }
      setSimilarGameMatches([]);
      setCreateGameDialogOpen(false);
      setNewGame({ name: "", boxArtUrl: "", releaseDate: "", publisher: "", developer: "", genres: [], completionEstimate: "", platforms: [], systems: [], region: "none", availableDigital: false, availablePhysical: false, contentTags: [], xboxTitleId: "", xboxBingId: "", steamAppId: "", npCommunicationId: "" });
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create game");
    }
  };

  // Open the Create Game dialog pre-filled from a user coverage request.
  // Maps the request's platform/system to the picker's slug/value format and
  // surfaces any similarly-named existing games so the admin can link instead.
  const handleAddGameFromRequest = async (req) => {
    const rp = (req.platform || "").toLowerCase();
    const matchPlatform = platforms.find(
      (p) => p.slug === req.platform || p.name?.toLowerCase() === rp || p.slug?.toLowerCase() === rp
    );
    const platformSlug = matchPlatform?.slug;
    const platformsArr = platformSlug ? [platformSlug] : [];
    let systemsArr = [];
    if (platformSlug && req.system) {
      const rs = req.system.toLowerCase();
      const sysOpts = systemOptions[platformSlug] || [];
      const matchSys = sysOpts.find(
        (s) => s.value === req.system || s.label?.toLowerCase() === rs || s.value?.toLowerCase() === rs
      );
      if (matchSys) systemsArr = [matchSys.value];
    }
    setNewGame({
      name: req.gameName || "",
      boxArtUrl: "", releaseDate: "", publisher: "", developer: "", genres: [],
      completionEstimate: "", platforms: platformsArr, systems: systemsArr, region: "none",
      availableDigital: false, availablePhysical: false, contentTags: [],
      xboxTitleId: "", xboxBingId: "", steamAppId: "", npCommunicationId: "",
    });
    setAddFromRequestId(req.id);
    setSimilarGameMatches([]);
    setPlatformCoverageOpen(false);
    setCreateGameDialogOpen(true);
    // Look up similar existing games (non-blocking). Keep exact-name matches —
    // a same-named game is exactly the duplicate the admin should be warned about.
    try {
      const { data } = await axios.get(`${API}/master/games`, {
        params: { search: req.gameName, limit: 6 },
      });
      setSimilarGameMatches(data.games || []);
    } catch (e) { /* non-fatal */ }
  };

  // Update game (or create it if editingGame.id is missing — the "Add Manually"
  // dropdown opens this same dialog in create mode so all master-game fields
  // can be captured in one pass instead of the previous two-step
  // add-then-edit round trip.)
  const handleUpdateGame = async () => {    if (!editingGame?.name?.trim()) {
      toast.error("Game name is required");
      return;
    }
    try {
      const payload = {
        ...editingGame,
        genres: Array.isArray(editingGame.genres) ? editingGame.genres : [],
        region: editingGame.region === "none" ? null : editingGame.region,
        contentTags: editingGame.contentTags || [],
        xboxTitleId: editingGame.xboxTitleId || null,
        xboxBingId: editingGame.xboxBingId || null,
        steamAppId: editingGame.steamAppId ? parseInt(editingGame.steamAppId) : null,
        npCommunicationId: editingGame.npCommunicationId || null,
        needsReview: editingGame.needsReview || false,
        reviewTypes: Array.isArray(editingGame.reviewTypes) ? editingGame.reviewTypes : [],
        showSystemBar: editingGame.showSystemBar || false,
        systemBarOverride: editingGame.systemBarOverride || null,
        challengeEligibility: editingGame.challengeEligibility || "eligible",
        challengeExcludedAchievements: editingGame.challengeExcludedAchievements || [],
      };
      delete payload._dateDisplay;

      const isCreate = !editingGame.id;
      if (isCreate) {
        // POST — id gets assigned server-side
        const response = await axios.post(`${API}/master/games`, payload);
        toast.success(`Created "${response.data.name}"`);
      } else {
        await axios.put(`${API}/master/games/${editingGame.id}`, payload);
        toast.success("Game updated");
      }
      setEditGameDialogOpen(false);
      setEditingGame(null);
      fetchGames();
      if (!isCreate && selectedGame?.id === editingGame.id) {
        fetchGameDetails(editingGame.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save game");
    }
  };

  // Open upload images dialog
  const openUploadImagesDialog = () => {
    setUploadOptions({ boxArt: false, dlcBoxArt: false, achievements: false });
    setUploadImagesDialogOpen(true);
  };

  // Upload images to Cloudinary
  const handleUploadImages = async () => {
    if (!selectedGame) return;
    if (!uploadOptions.boxArt && !uploadOptions.dlcBoxArt && !uploadOptions.achievements) {
      toast.error("Please select at least one option");
      return;
    }
    
    setUploadingImages(true);
    setUploadImagesDialogOpen(false);
    try {
      const response = await axios.post(`${API}/master/games/${selectedGame.id}/upload-images`, null, {
        params: {
          box_art: uploadOptions.boxArt,
          dlc_box_art: uploadOptions.dlcBoxArt,
          achievements: uploadOptions.achievements
        }
      });
      const result = response.data;
      
      const hasUploads = result.box_art_uploaded || 
                         result.dlc_box_art_uploaded > 0 || 
                         result.achievements_uploaded > 0;
      
      if (hasUploads) {
        toast.success(result.message);
        // Refresh to show updated images
        fetchGameDetails(selectedGame.id);
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  // ============ UNIFIED TAG MANAGEMENT ============
  
  const handleCreateUnifiedTag = async () => {
    if (!newTagName.trim()) {
      toast.error("Tag name is required");
      return;
    }
    try {
      await axios.post(`${API}/master/unified-tags`, null, {
        params: { name: newTagName.trim(), color: newTagColor, description: newTagDescription }
      });
      toast.success("Tag created");
      setNewTagName("");
      setNewTagColor("#64748B");
      setNewTagDescription("");
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tag");
    }
  };

  const handleUpdateUnifiedTag = async () => {
    if (!editingTag) return;
    try {
      await axios.put(`${API}/master/unified-tags/${editingTag.id}`, null, {
        params: { 
          name: editingTag.name, 
          color: editingTag.color, 
          description: editingTag.description 
        }
      });
      toast.success("Tag updated");
      setEditingTag(null);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update tag");
    }
  };

  const handleDeleteUnifiedTag = async (tagId, tagName) => {
    if (!confirm(`Delete tag "${tagName}"? It will be removed from all games.`)) return;
    try {
      await axios.delete(`${API}/master/unified-tags/${tagId}`);
      toast.success("Tag deleted");
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tag");
    }
  };

  // ============ BULK ACHIEVEMENT TAGGING ============
  
  const handleBulkTagUpdate = async (updates) => {
    if (!updates || updates.length === 0) {
      toast.info("No changes to apply");
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/master/games/${selectedGame.id}/achievements/bulk-tags`,
        { updates }
      );
      toast.success(`Updated tags on ${response.data.updated} achievements`);
      
      // Update local state — apply tag AND flag changes so reopening the
      // dialog shows the state the user just saved (fixes checkboxes not
      // staying checked after Apply).
      const updateMap = {};
      updates.forEach((u) => {
        updateMap[u.achievement_id] = u;
      });
      setSelectedGameAchievements((prev) =>
        prev.map((ach) => {
          const u = updateMap[ach.id];
          if (!u) return ach;
          const next = { ...ach };
          if (u.tags !== undefined) next.tags = u.tags;
          if (u.isSecret !== undefined) next.isSecret = u.isSecret;
          if (u.isPerCharacter !== undefined) next.isPerCharacter = u.isPerCharacter;
          return next;
        })
      );
      
      setShowBulkTagDialog(false);
      setSelectedAchievementIds([]);
      setAchBulkSelectMode(false);
    } catch (error) {
      console.error("Bulk tag update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update tags");
    }
  };

  const handleSingleAchievementTagUpdate = async (achId, newTags) => {
    try {
      await axios.put(`${API}/master/achievements/${achId}/tags`, { tags: newTags });
      toast.success("Tags updated");
      
      // Update local state without refetching (preserves order)
      setSelectedGameAchievements(prev => prev.map(ach => 
        ach.id === achId ? { ...ach, tags: newTags } : ach
      ));
    } catch (error) {
      console.error("Single tag update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update tags");
    }
  };

  // Bulk Publish/Unpublish
  const handleBulkPublishVisible = async () => {
    if (games.length === 0) {
      toast.error("No games to publish");
      return;
    }
    if (!confirm(`Publish ${games.length} visible games? They will appear in Browse and Sync.`)) return;

    try {
      const gameIds = games.map(g => g.id);
      const response = await axios.post(`${API}/master/games/bulk-publish`, {
        game_ids: gameIds,
        publish: true,
      });
      toast.success(response.data.message);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish games");
    }
  };

  const handleFixBadgeDates = async () => {
    if (!confirm("Fix badge unlock dates? This sets each per-character badge's date to its earliest character unlock (in your local time) and clears any stale time. Platform-verified dates are left untouched.")) return;
    try {
      toast.loading("Fixing badge dates...", { id: "fix-badge-dates" });
      const response = await axios.post(`${API}/maintenance/fix-badge-dates`);
      const { scanned = 0, updated = 0 } = response.data || {};
      toast.success(`Fixed ${updated} of ${scanned} per-character badge${scanned === 1 ? "" : "s"}.`, { id: "fix-badge-dates" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix badge dates", { id: "fix-badge-dates" });
    }
  };


  const handleBulkUnpublishVisible = async () => {
    if (games.length === 0) {
      toast.error("No games to unpublish");
      return;
    }
    if (!confirm(`Unpublish ${games.length} visible games? They will be hidden from Browse and Sync.`)) return;

    try {
      const gameIds = games.map(g => g.id);
      const response = await axios.post(`${API}/master/games/bulk-publish`, {
        game_ids: gameIds,
        publish: false,
      });
      toast.success(response.data.message);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unpublish games");
    }
  };

  // --- Multi-select bulk actions ---
  const toggleBulkMode = () => {
    setBulkMode(prev => {
      const next = !prev;
      if (!next) { selectedGamesCacheRef.current.clear(); setBulkSelectedIds(new Set()); }  // Leaving bulk mode → clear
      return next;
    });
  };

  const toggleBulkSelect = (gameId) => {
    // Cache the full game object (from the current page) so selections persist
    // across pages for tools that need more than the id.
    const obj = games.find((g) => g.id === gameId);
    if (obj) selectedGamesCacheRef.current.set(gameId, obj);
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      next.has(gameId) ? next.delete(gameId) : next.add(gameId);
      return next;
    });
  };

  const selectAllVisibleBulk = () => {
    // Add every game on the current page to the selection (union), preserving
    // anything already picked on other pages.
    games.forEach((g) => selectedGamesCacheRef.current.set(g.id, g));
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      games.forEach((g) => next.add(g.id));
      return next;
    });
  };

  const clearBulkSelection = () => { selectedGamesCacheRef.current.clear(); setBulkSelectedIds(new Set()); };

  const removeFromSelection = (gameId) => {
    selectedGamesCacheRef.current.delete(gameId);
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(gameId);
      return next;
    });
  };

  const runBulkAction = ({ title, description, confirmLabel, variant, url, body, successPrefix }) => {
    const ids = Array.from(bulkSelectedIds);
    if (ids.length === 0) {
      toast.error("Select at least one game first");
      return;
    }
    // Open the in-app confirmation dialog. The actual API call runs only
    // when the user clicks the confirm button — same UX intent as the old
    // window.confirm() but visible and testable.
    setBulkConfirm({
      title,
      description: description.replace("{n}", ids.length),
      confirmLabel,
      variant,
      onConfirm: async () => {
        setBulkRunning(true);
        try {
          const res = await axios.post(`${API}${url}`, {
            game_ids: ids,
            ...body,
          });
          toast.success(res.data.message || `${successPrefix} ${ids.length} games`);
          setBulkSelectedIds(new Set());
          fetchGames();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Bulk action failed");
        } finally {
          setBulkRunning(false);
          setBulkConfirm(null);
        }
      },
    });
  };

  const handleBulkMarkReview = (flag) => runBulkAction({
    title: flag ? "Mark games for review?" : "Clear review flag?",
    description: flag
      ? "Mark {n} selected games as needing review. They'll appear in the \"Needs Review\" filter."
      : "Clear the \"Needs Review\" flag from {n} selected games.",
    confirmLabel: flag ? "Mark for Review" : "Clear Review",
    variant: flag ? "warning" : "default",
    url: "/master/games/bulk-needs-review",
    body: { needs_review: flag },
    successPrefix: flag ? "Flagged" : "Cleared",
  });

  // Link all selected games as VERSIONS of the same game (one version group).
  const handleBulkLinkVersions = () => {
    if (bulkSelectedIds.size < 2) { toast.error("Select at least 2 games to link as versions"); return; }
    runBulkAction({
      title: "Link as versions?",
      description: "Link {n} selected games as versions of the same game. They collapse to a single Browse tile (primary = oldest record, changeable later). This does NOT affect multiplayer links.",
      confirmLabel: "Link Versions",
      variant: "default",
      url: "/master/version-link/selected",
      body: {},
      successPrefix: "Linked",
    });
  };

  // Mutually cross-play (multiplayer) link all selected games.
  const handleBulkLinkCrossPlay = () => {
    if (bulkSelectedIds.size < 2) { toast.error("Select at least 2 games to cross-play link"); return; }
    runBulkAction({
      title: "Cross-play link for multiplayer?",
      description: "Mutually cross-play link {n} selected games. Players of any linked version share billboards and matches. This is SEPARATE from version links.",
      confirmLabel: "Link Cross-Play",
      variant: "default",
      url: "/multiplayer/admin/cross-play-link-selected",
      body: {},
      successPrefix: "Cross-play linked",
    });
  };

  // Open the "Set Review Types" dialog. Pre-fills with the intersection of
  // currently-selected games' review types so partial states stay obvious.
  const openReviewTypesDialog = () => {
    if (bulkSelectedIds.size === 0) {
      toast.error("Select at least one game first");
      return;
    }
    // Compute the union of existing review types across the selected games
    // so the dialog hints at what's already flagged. The user can still
    // overwrite with any set they choose (set-exact semantics).
    const union = new Set();
    for (const g of getSelectedGameObjects()) {
      for (const t of (g.reviewTypes || [])) union.add(t);
    }
    setReviewTypesDialogValues(union);
    setReviewTypesDialogOpen(true);
  };

  const applyBulkReviewTypes = async () => {
    const ids = Array.from(bulkSelectedIds);
    if (ids.length === 0) return;
    const types = Array.from(reviewTypesDialogValues);
    try {
      setBulkRunning(true);
      const res = await axios.post(`${API}/master/games/bulk-review-types`, {
        game_ids: ids,
        review_types: types,
      });
      toast.success(res.data.message || `Updated ${ids.length} games`);
      setBulkSelectedIds(new Set());
      setReviewTypesDialogOpen(false);
      fetchGames();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update review tags");
    } finally {
      setBulkRunning(false);
    }
  };

  // Bulk "Set Platforms" / "Set Systems" — same set-exact semantics as
  // review types. Pre-fill each dialog with the union of currently-set
  // values across the selected games so partial overlaps stay visible.
  const openPlatformsDialog = () => {
    if (bulkSelectedIds.size === 0) {
      toast.error("Select at least one game first");
      return;
    }
    const union = new Set();
    for (const g of getSelectedGameObjects()) {
      for (const p of (g.platforms || [])) union.add(p);
    }
    setPlatformsDialogValues(union);
    setPlatformsDialogOpen(true);
  };
  const applyBulkPlatforms = async () => {
    const ids = Array.from(bulkSelectedIds);
    if (ids.length === 0) return;
    try {
      setBulkRunning(true);
      const res = await axios.post(`${API}/master/games/bulk-platforms`, {
        game_ids: ids,
        platforms: Array.from(platformsDialogValues),
      });
      toast.success(res.data.message || `Updated ${ids.length} games`);
      setBulkSelectedIds(new Set());
      setPlatformsDialogOpen(false);
      fetchGames();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set platforms");
    } finally {
      setBulkRunning(false);
    }
  };
  const openSystemsDialog = () => {
    if (bulkSelectedIds.size === 0) {
      toast.error("Select at least one game first");
      return;
    }
    const union = new Set();
    for (const g of getSelectedGameObjects()) {
      for (const s of (g.systems || [])) union.add(s);
    }
    setSystemsDialogValues(union);
    setSystemsDialogOpen(true);
  };
  const applyBulkSystems = async () => {
    const ids = Array.from(bulkSelectedIds);
    if (ids.length === 0) return;
    try {
      setBulkRunning(true);
      const res = await axios.post(`${API}/master/games/bulk-systems`, {
        game_ids: ids,
        systems: Array.from(systemsDialogValues),
      });
      toast.success(res.data.message || `Updated ${ids.length} games`);
      setBulkSelectedIds(new Set());
      setSystemsDialogOpen(false);
      fetchGames();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set systems");
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkPublishSelected = (publish) => runBulkAction({
    title: publish ? "Publish games?" : "Unpublish games?",
    description: publish
      ? "Publish {n} selected games. They'll become visible in Browse and Sync to user libraries."
      : "Unpublish {n} selected games. They'll be hidden from Browse and Sync.",
    confirmLabel: publish ? "Publish" : "Unpublish",
    variant: publish ? "success" : "danger",
    url: "/master/games/bulk-publish",
    body: { publish },
    successPrefix: publish ? "Published" : "Unpublished",
  });

  // DBox Update Functions
  const handleDboxPreview = async () => {
    if (!selectedGame) return;
    if (!selectedGame.xboxTitleId) {
      toast.error("This game has no Xbox Title ID - cannot fetch from DBox");
      return;
    }
    
    setDboxPreviewLoading(true);
    setDboxPreviewData(null);
    setDboxSelectedFields([]);
    setDboxApplyAchievements(false);
    setDboxCustomBoxArtUrl("");
    setDboxUpdateDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/master/games/${selectedGame.id}/dbox-preview`);
      setDboxPreviewData(response.data);
      
      // Auto-select fields that have new values and no conflicts (except boxArtUrl - let user paste manually)
      const autoSelect = [];
      const data = response.data;
      for (const field of ["developer", "publisher", "releaseDate", "description"]) {
        if (data.new_values[field] && !data.conflicts.includes(field)) {
          autoSelect.push(field);
        }
      }
      setDboxSelectedFields(autoSelect);
      
      // Auto-select achievements if available and none exist
      if (data.achievements_preview?.dbox_count > 0 && data.achievements_preview?.existing_count === 0) {
        setDboxApplyAchievements(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch DBox preview");
      setDboxUpdateDialogOpen(false);
    } finally {
      setDboxPreviewLoading(false);
    }
  };

  const handleDboxUpdate = async () => {
    if (!selectedGame || !dboxPreviewData) return;
    
    if (dboxSelectedFields.length === 0 && !dboxApplyAchievements && !dboxCustomBoxArtUrl) {
      toast.error("Please select at least one field, achievements, or provide a box art URL");
      return;
    }
    
    setDboxUpdateLoading(true);
    try {
      const response = await axios.post(`${API}/master/games/${selectedGame.id}/dbox-update`, {
        apply_fields: dboxSelectedFields,
        apply_achievements: dboxApplyAchievements,
        upload_images_to_cloudinary: dboxUploadToCloudinary,
        exophase_html: dboxExophaseHtml || null,
        apply_exophase_scores: dboxApplyExophaseScores,
        custom_box_art_url: dboxCustomBoxArtUrl || null
      });
      
      const result = response.data;
      
      let message = result.message;
      if (result.achievements?.images_matched > 0) {
        message += ` (${result.achievements.images_matched} images matched from Exophase, ${result.achievements.images_uploaded} uploaded)`;
      }
      if (result.achievements?.scores_overridden > 0) {
        message += ` · ${result.achievements.scores_overridden} score${result.achievements.scores_overridden === 1 ? '' : 's'} updated from Exophase`;
      }
      
      if (result.needs_review) {
        toast.warning(`Updated ${result.updated_fields.length} fields. Game flagged for review (missing: ${result.missing_fields.join(", ")})`);
      } else {
        toast.success(message);
      }
      
      // Refresh game details
      fetchGameDetails(selectedGame.id);
      setDboxUpdateDialogOpen(false);
      setDboxExophaseHtml(""); // Reset for next time
      setDboxCustomBoxArtUrl(""); // Reset for next time
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply DBox updates");
    } finally {
      setDboxUpdateLoading(false);
    }
  };

  const toggleDboxField = (field) => {
    setDboxSelectedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field) 
        : [...prev, field]
    );
  };

  // Unified Cover Art Lookup Functions (IGDB + SteamGridDB)
  const openCoverDialog = () => {
    if (selectedGame) {
      setCoverSearchQuery(selectedGame.name || "");
    }
    setCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    setSelectedCover(null);
    setCoverSourceFilter("all");
    setCoverDialogOpen(true);
  };

  const searchCovers = async () => {
    if (!coverSearchQuery.trim()) {
      toast.error("Enter a game name to search");
      return;
    }
    
    setCoverLoading(true);
    setCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    
    try {
      const response = await axios.get(`${API}/covers/search/${encodeURIComponent(coverSearchQuery.trim())}`);
      setCoverResults(response.data.results || { igdb: [], steamgriddb: [], combined: [] });
      
      if (response.data.total_combined === 0) {
        toast.info("No covers found");
      } else {
        toast.success(`Found ${response.data.total_combined} games with covers`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to search for covers");
    } finally {
      setCoverLoading(false);
    }
  };

  const applyCover = async () => {
    if (!selectedCover || !selectedGame) return;
    
    setCoverUploading(true);
    try {
      // Upload to Cloudinary first
      const uploadResponse = await axios.post(`${API}/cloudinary/upload-url`, {
        url: selectedCover.url,
        folder: "game_covers",
        public_id: `master_${selectedGame.id}_cover`
      });
      
      const cloudinaryUrl = uploadResponse.data.url;
      
      // Update the master game
      await axios.put(`${API}/master/games/${selectedGame.id}`, {
        ...selectedGame,
        boxArtUrl: cloudinaryUrl
      });
      
      toast.success("Box art updated successfully!");
      fetchGameDetails(selectedGame.id);
      setCoverDialogOpen(false);
    } catch (error) {
      // If Cloudinary upload fails, try using the source URL directly
      try {
        await axios.put(`${API}/master/games/${selectedGame.id}`, {
          ...selectedGame,
          boxArtUrl: selectedCover.url
        });
        toast.success(`Box art updated (using ${selectedCover.source} URL)`);
        fetchGameDetails(selectedGame.id);
        setCoverDialogOpen(false);
      } catch (err) {
        toast.error("Failed to update box art");
      }
    } finally {
      setCoverUploading(false);
    }
  };

  // Get filtered cover results based on source filter
  const getFilteredCoverResults = () => {
    if (coverSourceFilter === "all") {
      return coverResults.combined || [];
    } else if (coverSourceFilter === "igdb") {
      return coverResults.igdb || [];
    } else if (coverSourceFilter === "steamgriddb") {
      return coverResults.steamgriddb || [];
    }
    return [];
  };

  // DLC Cover Art Lookup Functions (IGDB + SteamGridDB)
  const openDlcCoverDialog = (dlc) => {
    setDlcForCoverSearch(dlc);
    // Use DLC name as search query, or game name + DLC name
    const searchQuery = dlc.name || `${selectedGame?.name} ${dlc.name}`;
    setDlcCoverSearchQuery(searchQuery);
    setDlcCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    setSelectedDlcCover(null);
    setDlcCoverSourceFilter("all");
    setDlcCoverDialogOpen(true);
  };

  const searchDlcCovers = async () => {
    if (!dlcCoverSearchQuery.trim()) {
      toast.error("Enter a search term");
      return;
    }
    
    setDlcCoverLoading(true);
    setDlcCoverResults({ igdb: [], steamgriddb: [], combined: [] });
    
    try {
      const response = await axios.get(`${API}/covers/search/${encodeURIComponent(dlcCoverSearchQuery.trim())}`);
      setDlcCoverResults(response.data.results || { igdb: [], steamgriddb: [], combined: [] });
      
      if (response.data.total_combined === 0) {
        toast.info("No covers found");
      } else {
        toast.success(`Found ${response.data.total_combined} results`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to search for covers");
    } finally {
      setDlcCoverLoading(false);
    }
  };

  const applyDlcCover = async () => {
    if (!selectedDlcCover || !dlcForCoverSearch || !selectedGame) return;
    
    setDlcCoverUploading(true);
    try {
      // Upload to Cloudinary first
      const uploadResponse = await axios.post(`${API}/cloudinary/upload-url`, {
        url: selectedDlcCover.url,
        folder: "dlc_covers",
        public_id: `master_${selectedGame.id}_dlc_${dlcForCoverSearch.id}_cover`
      });
      
      const cloudinaryUrl = uploadResponse.data.url;
      
      // Update the DLC (note: endpoint is /dlcs/ not /dlc/)
      await axios.put(`${API}/master/games/${selectedGame.id}/dlcs/${dlcForCoverSearch.id}`, {
        boxArtUrl: cloudinaryUrl
      });
      
      toast.success("DLC box art updated successfully!");
      fetchGameDetails(selectedGame.id);
      setDlcCoverDialogOpen(false);
    } catch (error) {
      console.error("DLC cover upload error:", error);
      // If Cloudinary upload fails, try using the source URL directly
      try {
        await axios.put(`${API}/master/games/${selectedGame.id}/dlcs/${dlcForCoverSearch.id}`, {
          boxArtUrl: selectedDlcCover.url
        });
        toast.success(`DLC box art updated (using ${selectedDlcCover.source} URL)`);
        fetchGameDetails(selectedGame.id);
        setDlcCoverDialogOpen(false);
      } catch (err) {
        console.error("DLC update error:", err);
        toast.error(err.response?.data?.detail || "Failed to update DLC box art");
      }
    } finally {
      setDlcCoverUploading(false);
    }
  };

  // Get filtered DLC cover results based on source filter
  const getFilteredDlcCoverResults = () => {
    if (dlcCoverSourceFilter === "all") {
      return dlcCoverResults.combined || [];
    } else if (dlcCoverSourceFilter === "igdb") {
      return dlcCoverResults.igdb || [];
    } else if (dlcCoverSourceFilter === "steamgriddb") {
      return dlcCoverResults.steamgriddb || [];
    }
    return [];
  };

  // Bug Reports Admin Functions
  const fetchBugReports = async () => {
    setBugReportsLoading(true);
    try {
      const response = await axios.get(`${API}/bug-reports`);
      setBugReports(response.data || []);
      setBugTotalCount((response.data || []).length);
    } catch (error) {
      toast.error("Failed to fetch bug reports");
    } finally {
      setBugReportsLoading(false);
    }
  };

  const markTutorialFeedbackRead = async () => {
    if (tutorialUnread === 0) return;
    try {
      await axios.post(`${API}/bug-reports/tutorial-mark-read`);
      setTutorialUnread(0);
    } catch (e) { /* non-blocking */ }
  };

  const fetchKnownIssuesAdmin = async () => {
    setKnownIssuesLoading(true);
    try {
      const response = await axios.get(`${API}/known-issues`);
      setKnownIssues(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch known issues");
    } finally {
      setKnownIssuesLoading(false);
    }
  };

  const handleUpdateBugReport = async (reportId, updates) => {
    try {
      await axios.put(`${API}/bug-reports/${reportId}`, updates);
      toast.success("Bug report updated");
      fetchBugReports();
      setEditingBugReport(null);
    } catch (error) {
      toast.error("Failed to update bug report");
    }
  };

  const handleDeleteBugReport = async (reportId) => {
    if (!window.confirm("Delete this bug report?")) return;
    try {
      await axios.delete(`${API}/bug-reports/${reportId}`);
      toast.success("Bug report deleted");
      fetchBugReports();
    } catch (error) {
      toast.error("Failed to delete bug report");
    }
  };

  const handleCreateKnownIssue = async () => {
    if (!newKnownIssue.title.trim() || !newKnownIssue.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    try {
      await axios.post(`${API}/known-issues`, newKnownIssue);
      toast.success("Known issue created");
      setNewKnownIssue({ title: "", description: "", status: "known", workaround: "", affectedAreas: [] });
      fetchKnownIssuesAdmin();
    } catch (error) {
      toast.error("Failed to create known issue");
    }
  };

  const handleUpdateKnownIssue = async (issueId, updates) => {
    try {
      await axios.put(`${API}/known-issues/${issueId}`, updates);
      toast.success("Known issue updated");
      fetchKnownIssuesAdmin();
      setEditingKnownIssue(null);
    } catch (error) {
      toast.error("Failed to update known issue");
    }
  };

  const handleDeleteKnownIssue = async (issueId) => {
    if (!window.confirm("Delete this known issue?")) return;
    try {
      await axios.delete(`${API}/known-issues/${issueId}`);
      toast.success("Known issue deleted");
      fetchKnownIssuesAdmin();
    } catch (error) {
      toast.error("Failed to delete known issue");
    }
  };

  // Platform Diagnostics Functions
  const fetchPlatformDiagnostics = async () => {
    setPlatformDiagnosticsLoading(true);
    try {
      const response = await axios.get(`${API}/master/diagnostics/platform-inconsistencies`);
      setPlatformDiagnostics(response.data);
    } catch (error) {
      toast.error("Failed to fetch platform diagnostics");
      console.error(error);
    } finally {
      setPlatformDiagnosticsLoading(false);
    }
  };

  const handleNormalizePlatforms = async () => {
    if (!window.confirm("This will normalize all platform names to their canonical forms (e.g., 'xbox' → 'Xbox'). Continue?")) return;
    setNormalizingPlatforms(true);
    try {
      const response = await axios.post(`${API}/master/diagnostics/normalize-platforms`);
      toast.success(response.data.message);
      // Refresh diagnostics
      await fetchPlatformDiagnostics();
      // Refresh games list
      fetchGames();
    } catch (error) {
      toast.error("Failed to normalize platforms");
      console.error(error);
    } finally {
      setNormalizingPlatforms(false);
    }
  };

  // Delete game
  const handleDeleteGame = async (gameId) => {
    if (!window.confirm("Delete this game and all its achievements?")) return;
    try {
      await axios.delete(`${API}/master/games/${gameId}`);
      toast.success("Game deleted");
      if (selectedGame?.id === gameId) {
        setSelectedGame(null);
        setSelectedGameAchievements([]);
      }
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete game");
    }
  };

  // Create achievement
  const handleCreateAchievement = async () => {
    if (!newAchievement.name.trim()) {
      toast.error("Achievement name is required");
      return;
    }
    try {
      // Find DLC name from ID if set
      let dlcName = null;
      if (newAchievement.dlcId) {
        const dlc = selectedGame.dlcs?.find(d => d.id === newAchievement.dlcId);
        dlcName = dlc?.name || null;
      }
      const payload = {
        ...newAchievement,
        dlcName,
      };
      await axios.post(`${API}/master/games/${selectedGame.id}/achievements`, payload);
      toast.success("Achievement created");
      setCreateAchievementDialogOpen(false);
      setNewAchievement({ name: "", description: "", imageUrl: "", score: 0, trophyType: null, isSecret: false, isPerCharacter: false, isMissable: false, isOnline: false, isUnobtainable: false, requiresExternalContent: false, dlcId: null, dlcName: "" });
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create achievement");
    }
  };

  // Update achievement
  const handleUpdateAchievement = async () => {
    if (!editingAchievement?.name?.trim()) {
      toast.error("Achievement name is required");
      return;
    }
    try {
      // Find DLC name from ID if set
      let dlcName = null;
      if (editingAchievement.dlcId) {
        const dlc = selectedGame.dlcs?.find(d => d.id === editingAchievement.dlcId);
        dlcName = dlc?.name || null;
      }
      const payload = {
        ...editingAchievement,
        dlcName,
      };
      const res = await axios.put(`${API}/master/games/${selectedGame.id}/achievements/${editingAchievement.id}`, payload);
      toast.success("Achievement updated");
      // Optimistically merge the server's canonical response so the next
      // Edit click shows the flags we just saved (fixes Secret / Per Char
      // checkboxes not staying checked when reopening the edit dialog).
      const updated = res?.data || payload;
      setSelectedGameAchievements((prev) =>
        prev.map((a) => (a.id === editingAchievement.id ? { ...a, ...updated } : a))
      );
      setEditAchievementDialogOpen(false);
      setEditingAchievement(null);
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update achievement");
    }
  };

  // Delete achievement
  const handleDeleteAchievement = async (achievementId) => {
    if (!window.confirm("Delete this achievement?")) return;
    try {
      await axios.delete(`${API}/master/games/${selectedGame.id}/achievements/${achievementId}`);
      toast.success("Achievement deleted");
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete achievement");
    }
  };

  // Bulk Move Achievements
  const handleBulkMoveAchievements = async () => {
    if (selectedAchievementIds.length === 0) return;
    
    try {
      const dlcId = bulkMoveTarget === "none" ? null : bulkMoveTarget;
      const dlcName = dlcId ? selectedGame.dlcs?.find(d => d.id === dlcId)?.name : null;
      
      // Update each achievement
      let moved = 0;
      for (const achId of selectedAchievementIds) {
        try {
          await axios.put(`${API}/master/games/${selectedGame.id}/achievements/${achId}`, {
            dlcId: dlcId,
            dlcName: dlcName
          });
          moved++;
        } catch (err) {
          console.error(`Failed to move achievement ${achId}:`, err);
        }
      }
      
      toast.success(`Moved ${moved} achievement${moved !== 1 ? 's' : ''} to ${dlcName || 'Base Game'}`);
      
      // Reset selection and refresh
      setSelectedAchievementIds([]);
      setAchBulkSelectMode(false);
      setBulkMoveDialogOpen(false);
      setBulkMoveTarget("none");
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error("Failed to move achievements");
    }
  };

  const toggleAchievementSelection = (achId) => {
    setSelectedAchievementIds(prev => 
      prev.includes(achId) 
        ? prev.filter(id => id !== achId)
        : [...prev, achId]
    );
  };

  const selectAllAchievements = () => {
    setSelectedAchievementIds(selectedGameAchievements.map(a => a.id));
  };

  const deselectAllAchievements = () => {
    setSelectedAchievementIds([]);
  };

  // Bulk Delete Achievements
  const handleBulkDeleteAchievements = async () => {
    if (selectedAchievementIds.length === 0) return;
    setBulkDeleteConfirmOpen(true);
  };

  const executeBulkDelete = async () => {
    setBulkDeleteConfirmOpen(false);
    let deleted = 0;
    for (const achId of selectedAchievementIds) {
      try {
        await axios.delete(`${API}/master/games/${selectedGame.id}/achievements/${achId}`);
        deleted++;
      } catch (err) {
        console.error(`Failed to delete achievement ${achId}:`, err);
        toast.error(`Failed to delete achievement: ${err.response?.data?.detail || err.message}`);
      }
    }
    
    toast.success(`Deleted ${deleted} achievement${deleted !== 1 ? 's' : ''}`);
    setSelectedAchievementIds([]);
    setAchBulkSelectMode(false);
    fetchGameDetails(selectedGame.id);
  };

  // Bulk Edit Achievements - Start
  const startBulkEditAchievements = () => {
    if (selectedAchievementIds.length === 0) return;
    
    // Get the selected achievements in order
    const queue = selectedGameAchievements.filter(a => selectedAchievementIds.includes(a.id));
    setBulkEditAchQueue(queue);
    setBulkEditAchIndex(0);
    setBulkEditAchCurrent({...queue[0]});
    setBulkEditAchDialogOpen(true);
  };

  // Bulk Edit - Save current and move to next
  const handleBulkEditAchSave = async () => {
    if (!bulkEditAchCurrent) return;
    
    try {
      await axios.put(`${API}/master/games/${selectedGame.id}/achievements/${bulkEditAchCurrent.id}`, bulkEditAchCurrent);
      toast.success(`Saved "${bulkEditAchCurrent.name}"`);
      
      // Move to next
      if (bulkEditAchIndex < bulkEditAchQueue.length - 1) {
        const nextIndex = bulkEditAchIndex + 1;
        setBulkEditAchIndex(nextIndex);
        setBulkEditAchCurrent({...bulkEditAchQueue[nextIndex]});
      } else {
        // Done - close dialog
        setBulkEditAchDialogOpen(false);
        setBulkEditAchQueue([]);
        setBulkEditAchCurrent(null);
        setBulkEditAchIndex(0);
        setSelectedAchievementIds([]);
        setAchBulkSelectMode(false);
        fetchGameDetails(selectedGame.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save achievement");
    }
  };

  // Bulk Edit - Skip current
  const handleBulkEditAchSkip = () => {
    if (bulkEditAchIndex < bulkEditAchQueue.length - 1) {
      const nextIndex = bulkEditAchIndex + 1;
      setBulkEditAchIndex(nextIndex);
      setBulkEditAchCurrent({...bulkEditAchQueue[nextIndex]});
    } else {
      // Done - close dialog
      setBulkEditAchDialogOpen(false);
      setBulkEditAchQueue([]);
      setBulkEditAchCurrent(null);
      setBulkEditAchIndex(0);
      setSelectedAchievementIds([]);
      setAchBulkSelectMode(false);
      fetchGameDetails(selectedGame.id);
    }
  };

  // Create DLC
  const handleCreateDlc = async () => {
    if (!newDlc.name.trim()) {
      toast.error("DLC name is required");
      return;
    }
    try {
      const payload = {
        ...newDlc,
        region: newDlc.region === "none" ? null : newDlc.region,
      };
      await axios.post(`${API}/master/games/${selectedGame.id}/dlcs`, payload);
      toast.success("DLC created");
      setCreateDlcDialogOpen(false);
      setNewDlc({ name: "", boxArtUrl: "", releaseDate: "", completionEstimate: "", region: "none" });
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create DLC");
    }
  };

  // Update DLC
  const handleUpdateDlc = async () => {
    if (!editingDlc?.name?.trim()) {
      toast.error("DLC name is required");
      return;
    }
    try {
      const payload = {
        ...editingDlc,
        region: editingDlc.region === "none" ? null : editingDlc.region,
      };
      await axios.put(`${API}/master/games/${selectedGame.id}/dlcs/${editingDlc.id}`, payload);
      toast.success("DLC updated");
      setEditDlcDialogOpen(false);
      setEditingDlc(null);
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update DLC");
    }
  };

  // Delete DLC
  const handleDeleteDlc = async (dlcId) => {
    if (!window.confirm("Delete this DLC and all its achievements?")) return;
    try {
      await axios.delete(`${API}/master/games/${selectedGame.id}/dlcs/${dlcId}`);
      toast.success("DLC deleted");
      fetchGameDetails(selectedGame.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete DLC");
    }
  };

  // Xbox Title ID lookup
  const handleXboxLookup = async (target) => {
    const gameName = target === 'new' ? newGame.name : editingGame?.name;
    const systems = target === 'new' ? newGame.systems : editingGame?.systems || [];
    
    if (!gameName?.trim()) {
      toast.error("Enter a game name first");
      return;
    }
    
    setXboxLookupTarget(target);
    setXboxLookupLoading(true);
    setXboxLookupResults([]);
    
    try {
      // Find Xbox system if selected
      const xboxSystem = systems.find(s => 
        s.includes('xbox') || s === 'windows-10'
      );
      
      const params = new URLSearchParams();
      params.append("game_name", gameName);
      if (xboxSystem) params.append("system", xboxSystem);
      
      const response = await axios.get(`${API}/xbox/oauth/lookup-title?${params}`);
      setXboxLookupResults(response.data.matches || []);
      setXboxLookupDialogOpen(true);

      if (response.data.matches?.length === 0) {
        if (response.data.historyAvailable === false) {
          toast.info("No matches found. Connect your Xbox account (Settings → Platform Links) for more results, or check the game name spelling.");
        } else {
          toast.info("No matching titles found in your Xbox history or the Master Database.");
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Not signed in. Please log in again.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to lookup Xbox title");
      }
    } finally {
      setXboxLookupLoading(false);
    }
  };

  const selectXboxTitle = (title) => {
    if (xboxLookupTarget === 'new') {
      setNewGame({
        ...newGame, 
        xboxTitleId: title.titleId,
        boxArtUrl: newGame.boxArtUrl || title.displayImage || "",
      });
    } else {
      setEditingGame({
        ...editingGame, 
        xboxTitleId: title.titleId,
        boxArtUrl: editingGame.boxArtUrl || title.displayImage || "",
      });
    }
    setXboxLookupDialogOpen(false);
    toast.success(`Xbox Title ID set: ${title.titleId}`);
  };

  // Steam App ID lookup
  const handleSteamLookup = async (target) => {
    const gameName = target === 'new' ? newGame.name : editingGame?.name;
    
    if (!gameName?.trim()) {
      toast.error("Enter a game name first");
      return;
    }
    
    setSteamLookupTarget(target);
    setSteamLookupLoading(true);
    setSteamLookupResults([]);
    
    try {
      const params = new URLSearchParams();
      params.append("game_name", gameName);
      
      const response = await axios.get(`${API}/steam/lookup-appid?${params}`);
      setSteamLookupResults(response.data.matches || []);
      setSteamLookupDialogOpen(true);
      
      if (response.data.matches?.length === 0) {
        toast.info("No matching Steam games found.");
      }
    } catch (error) {
      if (error.response?.status === 500) {
        toast.error("Steam API not configured. Check Steam API key in backend.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to lookup Steam game");
      }
    } finally {
      setSteamLookupLoading(false);
    }
  };

  const selectSteamGame = (game) => {
    if (steamLookupTarget === 'new') {
      setNewGame({
        ...newGame, 
        steamAppId: String(game.appId),
        boxArtUrl: newGame.boxArtUrl || game.displayImage || "",
      });
    } else {
      setEditingGame({
        ...editingGame, 
        steamAppId: String(game.appId),
        boxArtUrl: editingGame.boxArtUrl || game.displayImage || "",
      });
    }
    setSteamLookupDialogOpen(false);
    toast.success(`Steam App ID set: ${game.appId}`);
  };

  // Helper to get genre display name from slug
  const getGenreDisplayName = (slug) => {
    const genre = genres.find(g => g.slug === slug);
    return genre ? genre.name : slug;
  };

  // PlayStation NP Communication ID lookup
  const handlePsnLookup = async (target) => {
    const gameName = target === 'new' ? newGame.name : editingGame?.name;
    const systems = target === 'new' ? newGame.systems : editingGame?.systems || [];
    
    if (!gameName?.trim()) {
      toast.error("Enter a game name first");
      return;
    }
    
    setPsnLookupTarget(target);
    setPsnLookupLoading(true);
    setPsnLookupResults([]);
    
    try {
      // Find PlayStation system if selected
      const psnSystem = systems.find(s => 
        s.includes('ps') || s === 'ps-vita' || s === 'psp'
      );
      
      const params = new URLSearchParams();
      params.append("game_name", gameName);
      if (psnSystem) params.append("system", psnSystem);
      
      const response = await axios.get(`${API}/playstation/lookup-npid?${params}`);
      setPsnLookupResults(response.data.matches || []);
      setPsnLookupDialogOpen(true);
      
      if (response.data.matches?.length === 0) {
        toast.info("No matching PlayStation games found in your trophy list.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Not signed in to PlayStation. Go to Settings → Platform Links.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to lookup PlayStation game");
      }
    } finally {
      setPsnLookupLoading(false);
    }
  };

  const selectPsnGame = (game) => {
    if (psnLookupTarget === 'new') {
      setNewGame({
        ...newGame, 
        npCommunicationId: game.npCommunicationId,
        boxArtUrl: newGame.boxArtUrl || game.displayImage || "",
      });
    } else {
      setEditingGame({
        ...editingGame, 
        npCommunicationId: game.npCommunicationId,
        boxArtUrl: editingGame.boxArtUrl || game.displayImage || "",
      });
    }
    setPsnLookupDialogOpen(false);
    toast.success(`PlayStation NP ID set: ${game.npCommunicationId}`);
  };

  // Genre remapping function
  const handleRemapGenre = (oldGenreSlug) => {
    setGenreToRemap(oldGenreSlug);
    setRemapTargetGenre("");
    setGenreRemapDialogOpen(true);
  };

  const confirmGenreRemap = () => {
    if (!genreToRemap || !remapTargetGenre) return;
    
    const currentGenres = editingGame.genres || [];
    // Remove old genre, add new one (if not already present)
    const newGenres = currentGenres.filter(g => g !== genreToRemap);
    if (!newGenres.includes(remapTargetGenre)) {
      newGenres.push(remapTargetGenre);
    }
    
    setEditingGame({...editingGame, genres: newGenres});
    setGenreRemapDialogOpen(false);
    setGenreToRemap(null);
    toast.success(`Remapped "${genreToRemap}" to "${remapTargetGenre}"`);
  };

  // Exophase Import functions
  const handleExophaseScrape = async () => {
    if (exophaseInputMode === "url" && !exophaseUrl.trim()) {
      toast.error("Please enter an Exophase URL");
      return;
    }
    if (exophaseInputMode === "html" && !exophaseHtml.trim()) {
      toast.error("Please paste the HTML content");
      return;
    }
    
    setExophaseLoading(true);
    setExophaseAchievements([]);
    
    try {
      const payload = {
        gameId: selectedGame?.id
      };
      
      if (exophaseInputMode === "url") {
        payload.url = exophaseUrl;
      } else {
        payload.htmlContent = exophaseHtml;
      }
      
      const response = await axios.post(`${API}/exophase/scrape`, payload);
      
      if (response.data.success && response.data.achievements.length > 0) {
        setExophaseAchievements(response.data.achievements);
        toast.success(`Found ${response.data.achievements.length} achievements`);
      } else {
        toast.error("No achievements found on this page");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to scrape Exophase page";
      toast.error(errorMsg);
      // If it's a 403 error, suggest switching to HTML mode
      if (error.response?.status === 403) {
        setExophaseInputMode("html");
      }
    } finally {
      setExophaseLoading(false);
    }
  };

  // Handle images-only bulk update
  const handleExophaseImagesOnlyUpdate = async () => {
    if (!selectedGame || exophaseAchievements.length === 0) return;

    setExophaseImporting(true);
    setExophaseImportProgress(10);

    // Snapshot existing image URLs so we can detect "request timed out but the
    // backend kept working and finished" — common on very large batches.
    let preUpdateImages = new Map();
    try {
      const pre = await axios.get(`${API}/master/games/${selectedGame.id}/achievements`);
      for (const a of (pre.data || [])) preUpdateImages.set((a.name || "").toLowerCase().trim(), a.imageUrl || "");
    } catch { /* non-fatal */ }

    const incomingByName = new Map();
    for (const a of exophaseAchievements) {
      if (a.name && a.imageUrl) incomingByName.set(a.name.toLowerCase().trim(), a.imageUrl);
    }

    setExophaseImportProgress(20);

    try {
      const response = await axios.post(`${API}/exophase/bulk-update-images`, {
        masterGameId: selectedGame.id,
        scrapedAchievements: exophaseAchievements.map(a => ({
          name: a.name,
          imageUrl: a.imageUrl,
          score: a.score
        })),
        applyScores: !!exophaseApplyScores
      }, { timeout: 0 });

      setExophaseImportProgress(100);

      const results = response.data.results;
      const scoresMsg = (exophaseApplyScores && results.scoresUpdated)
        ? ` · ${results.scoresUpdated} score${results.scoresUpdated === 1 ? '' : 's'} updated`
        : '';
      toast.success(
        `Images updated! ${results.updated} updated, ${results.not_found} not matched, ${results.failed} failed${scoresMsg}`,
        { duration: 6000 }
      );

      // Refresh the game details to show new images
      await fetchGameDetails(selectedGame.id);

      // Close dialog and reset
      setExophaseDialogOpen(false);
      setExophaseAchievements([]);
      setExophaseUrl("");
      setExophaseHtml("");
      setExophaseImagesOnly(false);
      setExophaseApplyScores(false);
    } catch (error) {
      // Backend may still be running — re-fetch and compare. If images actually
      // changed, treat it as success even though the HTTP request errored.
      console.error("Bulk image update error:", error);
      let recovered = 0;
      try {
        // Give the backend a moment to finish in case the request just timed out
        await new Promise(r => setTimeout(r, 4000));
        const post = await axios.get(`${API}/master/games/${selectedGame.id}/achievements`);
        for (const a of (post.data || [])) {
          const key = (a.name || "").toLowerCase().trim();
          const before = preUpdateImages.get(key);
          const incoming = incomingByName.get(key);
          if (incoming && before !== a.imageUrl) {
            recovered += 1;
          }
        }
      } catch { /* ignore */ }

      if (recovered > 0) {
        toast.success(
          `Images updated! ${recovered} achievement${recovered === 1 ? '' : 's'} refreshed (the server kept processing after the request timed out).`,
          { duration: 7000 }
        );
        await fetchGameDetails(selectedGame.id);
        setExophaseDialogOpen(false);
        setExophaseAchievements([]);
        setExophaseUrl("");
        setExophaseHtml("");
        setExophaseImagesOnly(false);
        setExophaseApplyScores(false);
      } else {
        toast.error(error.response?.data?.detail || error.message || "Failed to update images");
      }
    } finally {
      setExophaseImporting(false);
      setExophaseImportProgress(0);
    }
  };

  const handleExophaseImport = async () => {
    if (!selectedGame || exophaseAchievements.length === 0) return;
    
    // If images-only mode, use the bulk update endpoint
    if (exophaseImagesOnly) {
      await handleExophaseImagesOnlyUpdate();
      return;
    }

    // If "Add New Only" mode, fetch existing master achievements and filter out
    // anything that already exists by normalized name. This lets the user re-parse
    // an Exophase URL after a DLC update without duplicating the originals.
    let achievementsToImport = exophaseAchievements;
    if (exophaseAddNewOnly) {
      try {
        const existingRes = await axios.get(`${API}/master/games/${selectedGame.id}/achievements`);
        const norm = (s) => (s || "").toLowerCase().replace(/[^\w\s]/g, "").trim();
        const existingNames = new Set((existingRes.data || []).map(a => norm(a.name)));
        achievementsToImport = exophaseAchievements.filter(a => !existingNames.has(norm(a.name)));
        const skipped = exophaseAchievements.length - achievementsToImport.length;
        if (achievementsToImport.length === 0) {
          toast.info(`All ${skipped} achievements already exist in the master DB — nothing to add.`);
          return;
        }
        if (skipped > 0) {
          toast.info(`Skipping ${skipped} achievement${skipped === 1 ? '' : 's'} already in the master DB; adding ${achievementsToImport.length} new one${achievementsToImport.length === 1 ? '' : 's'}.`);
        }
      } catch (err) {
        toast.error("Failed to check existing achievements — aborting.");
        return;
      }
    }

    setExophaseImporting(true);
    setExophaseImportProgress(0);

    const total = achievementsToImport.length;
    let imported = 0;
    let failed = 0;

    // Step 1: Batch upload all images with deduplication
    const achievementsWithImages = achievementsToImport.filter(a => a.imageUrl);
    let imageUrlMap = {}; // Map original URL to Cloudinary URL
    
    if (achievementsWithImages.length > 0) {
      try {
        const uploadResponse = await axios.post(`${API}/exophase/upload-images`, {
          achievements: achievementsWithImages.map(a => ({
            name: a.name,
            description: a.description,
            score: a.score,
            imageUrl: a.imageUrl
          })),
          gameName: selectedGame.name,
          platform: selectedGame.platform || "",
          system: selectedGame.system || selectedGame.primarySystem || ""
        });
        
        if (uploadResponse.data.success) {
          // Build map of original URL to Cloudinary URL
          for (const result of uploadResponse.data.results) {
            if (result.success && result.cloudinaryUrl) {
              imageUrlMap[result.originalUrl] = result.cloudinaryUrl;
            }
          }
          
          const deduped = uploadResponse.data.deduplicated || 0;
          const unique = uploadResponse.data.uniqueUploads || 0;
          if (deduped > 0) {
            toast.info(`Images: ${unique} unique uploads, ${deduped} duplicates reused`);
          }
        }
      } catch (uploadError) {
        console.error("Batch image upload failed:", uploadError);
        toast.warning("Image upload partially failed, continuing with original URLs");
      }
    }
    
    setExophaseImportProgress(20); // 20% for image upload phase
    
    // Step 2: Create achievements with uploaded images
    for (const ach of achievementsToImport) {
      try {
        // Use Cloudinary URL if available, otherwise fall back to original
        const imageUrl = imageUrlMap[ach.imageUrl] || ach.imageUrl;
        
        // Create the achievement
        await axios.post(`${API}/master/games/${selectedGame.id}/achievements`, {
          name: ach.name,
          description: ach.description,
          score: ach.score,
          imageUrl: imageUrl,
          isHidden: false
        });
        
        imported++;
      } catch (error) {
        console.error(`Failed to import ${ach.name}:`, error);
        failed++;
      }
      
      // Progress: 20% for images + 80% for achievements
      setExophaseImportProgress(20 + Math.round(((imported + failed) / total) * 80));
    }
    
    setExophaseImporting(false);
    
    if (imported > 0) {
      toast.success(`Imported ${imported} achievements${failed > 0 ? `, ${failed} failed` : ''}`);
      // Refresh achievements list
      if (selectedGame) {
        fetchGameDetails(selectedGame.id);
      }
    } else {
      toast.error(`Failed to import achievements`);
    }
    
    // Close dialog and reset
    setExophaseDialogOpen(false);
    setExophaseUrl("");
    setExophaseAchievements([]);
    setExophaseImportProgress(0);
  };

  // XboxDB Scraper functions
  const startXboxDbScrape = async () => {
    setScraperLoading(true);
    try {
      await axios.post(`${API}/scraper/xboxdb/start`);
      toast.success("XboxDB scrape started! This will import ~1700 Xbox 360 games.");
      // Start polling for status
      pollScraperStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start scraper");
      setScraperLoading(false);
    }
  };

  const pollScraperStatus = async () => {
    try {
      const response = await axios.get(`${API}/scraper/xboxdb/status`);
      setScraperStatus(response.data);
      
      if (response.data.in_progress) {
        // Continue polling
        setTimeout(pollScraperStatus, 2000);
      } else {
        setScraperLoading(false);
        if (response.data.completed) {
          toast.success(`Scrape complete! Imported ${response.data.games_imported} games.`);
          fetchGames(); // Refresh the list
        }
      }
    } catch (error) {
      console.error("Failed to get scraper status:", error);
      setScraperLoading(false);
    }
  };

  const checkScraperStatus = async () => {
    try {
      const response = await axios.get(`${API}/scraper/xboxdb/status`);
      setScraperStatus(response.data);
      if (response.data.in_progress) {
        setScraperLoading(true);
        pollScraperStatus();
      }
    } catch (error) {
      // Scraper status not available, that's ok
    }
  };

  // Check scraper status on mount
  useEffect(() => {
    checkScraperStatus();
  }, []);

  // Import from library
  const fetchLibraryGames = async () => {
    setLibraryLoading(true);
    try {
      const params = new URLSearchParams();
      if (librarySearch) params.append("search", librarySearch);
      params.append("limit", "50");
      const response = await axios.get(`${API}/games?${params}`);
      setLibraryGames(response.data);
    } catch (error) {
      console.error("Failed to fetch library games:", error);
    } finally {
      setLibraryLoading(false);
    }
  };


  const handleImportFromLibrary = async (gameId, gameName) => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/master/import-from-library/${gameId}`);
      const { achievementsImported, dlcsImported } = response.data;
      const dlcText = dlcsImported > 0 ? `, ${dlcsImported} DLCs` : "";
      toast.success(`Imported "${gameName}" with ${achievementsImported} achievements${dlcText}`);
      setImportFromLibraryDialogOpen(false);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import game");
    } finally {
      setImporting(false);
    }
  };

  // Export database
  const handleExportDatabase = async () => {
    try {
      const response = await axios.get(`${API}/master/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `master_database_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${response.data.stats.totalGames} games, ${response.data.stats.totalAchievements} achievements`);
    } catch (error) {
      toast.error("Failed to export database");
    }
  };

  // Import database
  const handleImportDatabase = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }
    setImportingDb(true);
    try {
      const text = await importFile.text();
      const data = JSON.parse(text);
      const response = await axios.post(`${API}/master/import?merge_strategy=${importMergeStrategy}`, data);
      const stats = response.data.stats;
      toast.success(`Imported: ${stats.gamesImported} games, ${stats.achievementsImported} achievements. Skipped: ${stats.gamesSkipped}`);
      setImportDialogOpen(false);
      setImportFile(null);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import database");
    } finally {
      setImportingDb(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchConfig();
  }, []);
  
  // Debounce search query - wait 400ms after user stops typing
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);
  
  useEffect(() => {
    fetchGames();
  }, [debouncedSearchQuery, platformFilter, systemFilter, releaseYearFilter, regionFilter, missingFilter, reviewTypesFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    if (importFromLibraryDialogOpen) {
      fetchLibraryGames();
    }
  }, [importFromLibraryDialogOpen, librarySearch]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set("search", debouncedSearchQuery);
    if (platformFilter !== "all") params.set("platform", platformFilter);
    if (systemFilter !== "all") params.set("system", systemFilter);
    if (releaseYearFilter !== "all") params.set("year", releaseYearFilter);
    if (regionFilter !== "all") params.set("region", regionFilter);
    if (missingFilter !== "all") params.set("missing", missingFilter);
    if (sortBy !== "name") params.set("sortBy", sortBy);
    if (sortOrder !== "asc") params.set("sortOrder", sortOrder);
    if (currentPage > 1) params.set("page", currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [debouncedSearchQuery, platformFilter, systemFilter, releaseYearFilter, regionFilter, missingFilter, reviewTypesFilter, sortBy, sortOrder, currentPage]);
  
  // Copy game handler
  const handleCopyGame = (gameId, gameName) => {
    setCopyDialog({ open: true, gameId, gameName: gameName || "" });
  };

  const doCopyGame = async (linked) => {
    const gameId = copyDialog.gameId;
    setCopyDialog({ open: false, gameId: null, gameName: "" });
    if (!gameId) return;
    try {
      const response = await axios.post(`${API}/master/games/${gameId}/copy`, null, { params: { linked } });
      toast.success(`${linked ? "Linked copy" : "Copy"} created with ${response.data.dlcsCopied} DLCs — re-parse achievements from source to populate`);
      fetchGames();
      // Select the new game
      fetchGameDetails(response.data.game.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy game");
    }
  };
  
  // Generate year options for filter (last 50 years)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 50; y--) {
      years.push(y);
    }
    return years;
  };

  const totalPages = Math.ceil(totalGames / gamesPerPage);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Database className="w-8 h-8 text-purple-400" />
            Master Database
          </h1>
          <p className="text-white/60 mt-1">
            {totalGames} games in canonical database
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Community Management */}
          <Button
            variant="outline"
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
            onClick={() => navigate("/master-database/community")}
            data-testid="community-management-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Community
            {(pendingCounts.challenges + pendingCounts.organizers + (pendingCounts.planners || 0) + pendingCounts.facts) > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {pendingCounts.challenges + pendingCounts.organizers + (pendingCounts.planners || 0) + pendingCounts.facts}
              </span>
            )}
          </Button>
          
          {/* Members Button */}
          <Button
            variant="outline"
            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            onClick={() => setMembersDialogOpen(true)}
            data-testid="members-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Members
          </Button>
          
          {/* Add Game Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-500" data-testid="add-game-dropdown-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Game
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1A1A] border-white/10">
              <DropdownMenuItem 
                onClick={() => setImportFromLibraryDialogOpen(true)}
                className="text-white hover:bg-white/10 cursor-pointer"
              >
                <Library className="w-4 h-4 mr-2" />
                Import from Library
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setImportDialogOpen(true)}
                className="text-white hover:bg-white/10 cursor-pointer"
              >
                <FileJson className="w-4 h-4 mr-2" />
                Import from JSON
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={() => {
                  // Open the full-featured Edit Game Dialog in create mode so
                  // publishers, developers, ratings, tags, systems, links,
                  // etc. can all be entered up-front — instead of the older
                  // trimmed "Add Master Game" form that was missing most
                  // fields and required a follow-up Edit round-trip.
                  setEditingGame({
                    name: "",
                    boxArtUrl: "",
                    releaseDate: "",
                    publisher: "",
                    developer: "",
                    publishers: [],
                    developers: [],
                    ratings: [],
                    genres: [],
                    completionEstimate: "",
                    platforms: [],
                    systems: [],
                    primarySystem: null,
                    region: "none",
                    availableDigital: false,
                    availablePhysical: false,
                    contentTags: [],
                    tags: [],
                    xboxTitleId: "",
                    xboxBingId: "",
                    steamAppId: "",
                    npCommunicationId: "",
                    hasNoAchievements: false,
                    isAchievementSlop: false,
                    needsReview: false,
                    reviewTypes: [],
                    showSystemBar: false,
                    systemBarOverride: null,
                    challengeEligibility: "eligible",
                    challengeExcludedAchievements: [],
                  });
                  setEditGameDialogOpen(true);
                }}
                className="text-white hover:bg-white/10 cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Manually
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Export */}
          <Button 
            variant="outline" 
            className="border-white/20 text-white/70 hover:text-white"
            onClick={handleExportDatabase}
            data-testid="export-db-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          
          {/* Config */}
          <Button 
            variant="outline" 
            className="border-white/20 text-white/70 hover:text-white"
            onClick={() => navigate("/master-database/config")}
            data-testid="config-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Config
          </Button>
          
          {/* Manage Links */}
          <Button 
            variant="outline" 
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            onClick={() => navigate("/master-database/links")}
            data-testid="manage-links-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Links
          </Button>
          
          {/* Manage Tags */}
          <Button 
            variant="outline" 
            className="border-teal-500/30 text-teal-400 hover:bg-teal-500/10"
            onClick={() => navigate("/app/master-database/tags")}
            data-testid="manage-tags-btn"
          >
            <Tag className="w-4 h-4 mr-2" />
            Tags
          </Button>
          
          {/* Milestones */}
          <Button 
            variant="outline" 
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            onClick={() => navigate("/app/master-database/milestones")}
            data-testid="manage-milestones-btn"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Milestones
          </Button>

          {/* Duplicate Image Finder */}
          <Button
            variant="outline"
            className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
            onClick={() => navigate("/app/master-database/images")}
            data-testid="dup-images-btn"
          >
            <Images className="w-4 h-4 mr-2" />
            Images
          </Button>
          {/* Platform Coverage */}
          <Button 
            variant="outline" 
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 relative"
            onClick={() => setPlatformCoverageOpen(true)}
            data-testid="platform-coverage-btn"
          >
            <Layers className="w-4 h-4 mr-2" />
            Coverage
            {gameRequestCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold px-1" data-testid="coverage-request-badge">
                {gameRequestCount}
              </span>
            )}
          </Button>

          {/* Custom Platform/System Requests */}
          <Button
            variant="outline"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 relative"
            onClick={() => setPsRequestsOpen(true)}
            data-testid="ps-requests-btn"
          >
            <Server className="w-4 h-4 mr-2" />
            Platform Requests
            {psRequestCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold px-1" data-testid="ps-request-badge">
                {psRequestCount}
              </span>
            )}
          </Button>
          
          {/* Bug Reports Admin */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 relative"
                data-testid="bug-admin-btn"
              >
                <Bug className="w-4 h-4 mr-2" />
                Bugs
                {bugTotalCount > 0 && (
                  <span
                    className="ml-2 bg-amber-500 text-black text-xs font-bold px-1.5 py-0.5 rounded-full"
                    data-testid="bug-total-count-badge"
                    title={`${bugTotalCount} bug report${bugTotalCount === 1 ? "" : "s"} in the list`}
                  >
                    {bugTotalCount}
                  </span>
                )}
                {tutorialUnread > 0 && (
                  <span
                    className="ml-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full"
                    data-testid="bug-tutorial-unread-badge"
                    title={`${tutorialUnread} new tutorial feedback`}
                  >
                    {tutorialUnread}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1A1A] border-white/10">
              <DropdownMenuItem 
                onClick={() => { fetchBugReports(); setBugReportsDialogOpen(true); markTutorialFeedbackRead(); }}
                className="text-white hover:bg-white/10 cursor-pointer"
              >
                <Bug className="w-4 h-4 mr-2" />
                View Bug Reports
                <span className="ml-auto flex items-center gap-1">
                  {bugTotalCount > 0 && (
                    <span
                      className="bg-amber-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      data-testid="bug-menu-total-count-badge"
                    >
                      {bugTotalCount}
                    </span>
                  )}
                  {tutorialUnread > 0 && (
                    <span
                      className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      data-testid="bug-menu-tutorial-unread-badge"
                    >
                      {tutorialUnread} new
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { fetchKnownIssuesAdmin(); setKnownIssuesDialogOpen(true); }}
                className="text-white hover:bg-white/10 cursor-pointer"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Manage Known Issues
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleBulkPublishVisible}
                className="text-green-400 hover:bg-green-500/10 cursor-pointer"
              >
                <Eye className="w-4 h-4 mr-2" />
                Publish All Visible ({games.length})
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleBulkUnpublishVisible}
                className="text-red-400 hover:bg-red-500/10 cursor-pointer"
              >
                <EyeOff className="w-4 h-4 mr-2" />
                Unpublish All Visible ({games.length})
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={() => { fetchPlatformDiagnostics(); setPlatformDiagnosticsOpen(true); }}
                className="text-cyan-400 hover:bg-cyan-500/10 cursor-pointer"
              >
                <Database className="w-4 h-4 mr-2" />
                Platform Diagnostics
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleFixBadgeDates}
                className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                data-testid="fix-badge-dates-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Fix Badge Unlock Dates
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Import from Library Dialog */}
      <Dialog open={importFromLibraryDialogOpen} onOpenChange={setImportFromLibraryDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import from Your Library</DialogTitle>
            <DialogDescription className="text-white/60">
              Select a game from your library to import into the master database
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="Search your library..."
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              className="bg-black/50 border-white/10"
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {libraryLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                </div>
              ) : !libraryGames || libraryGames.length === 0 ? (
                <p className="text-center text-white/50 py-8">No games found</p>
              ) : (
                libraryGames?.map((game) => (
                  <div key={game.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                    <img 
                      src={game.boxArtUrl || "/placeholder-game.png"} 
                      alt={game.name}
                      className="w-12 h-12 rounded object-contain bg-black/50"
                      onError={(e) => e.target.src = "/placeholder-game.png"}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{game.name}</p>
                      <p className="text-xs text-white/50">
                        {game.platform} • {game.totalAchievements || 0} achievements
                        {game.dlcCount > 0 && ` • ${game.dlcCount} DLCs`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleImportFromLibrary(game.id, game.name)}
                      disabled={importing}
                      className="bg-green-600 hover:bg-green-500"
                    >
                      {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Import JSON Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Import Master Database</DialogTitle>
            <DialogDescription className="text-white/60">
              Import games and achievements from a JSON file
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>JSON File</Label>
              <Input
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Import Strategy</Label>
              <Select value={importMergeStrategy} onValueChange={setImportMergeStrategy}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">Import all (recommended for restores)</SelectItem>
                  <SelectItem value="skip">Skip games with same name</SelectItem>
                  <SelectItem value="overwrite">Overwrite games with same name</SelectItem>
                  <SelectItem value="merge">Merge achievements into same-name games</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-white/40">
                "Import all" preserves games with same name on different platforms/systems.
              </p>
            </div>
            <Button 
              onClick={handleImportDatabase} 
              disabled={!importFile || importingDb}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              {importingDb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Database
            </Button>
          </div>
        </DialogContent>
      </Dialog>
          
      {/* Create Game Dialog */}
      <CreateGameDialog
        open={createGameDialogOpen}
        onOpenChange={(v) => { setCreateGameDialogOpen(v); if (!v) { setSimilarGameMatches([]); setAddFromRequestId(null); } }}
        newGame={newGame}
        onNewGameChange={setNewGame}
        similarMatches={similarGameMatches}
        onCreate={handleCreateGame}
        platforms={platforms}
        genres={genres}
        contentTags={contentTags}
        systemOptions={systemOptions}
        getAvailableContentTags={getAvailableContentTags}
        regionOptions={regionOptions}
        onXboxLookup={handleXboxLookup}
        xboxLookupLoading={xboxLookupLoading}
        onSteamLookup={handleSteamLookup}
        steamLookupLoading={steamLookupLoading}
        onPsnLookup={handlePsnLookup}
        psnLookupLoading={psnLookupLoading}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Search master database..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="pl-10 bg-black/50 border-white/10 text-white"
            data-testid="master-db-search"
          />
        </div>
        
        {/* Filters Button */}
        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className="bg-black/50 border-white/10 justify-start"
              data-testid="master-db-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              <span>Filters</span>
              {(platformFilter !== "all" || systemFilter !== "all" || releaseYearFilter !== "all" || missingFilter !== "all" || reviewTypesFilter.length > 0) && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-500/30 rounded">
                  {[platformFilter !== "all", systemFilter !== "all", releaseYearFilter !== "all", regionFilter !== "all", missingFilter !== "all", reviewTypesFilter.length > 0].filter(Boolean).length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-[#0A0A0A] border-white/10 p-0" align="start">
            <div className="p-3 border-b border-white/10">
              <h4 className="font-medium text-white text-sm">Filter Games</h4>
            </div>
            
            {/* Platform Filter */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Platform</p>
              <Select value={platformFilter} onValueChange={(v) => { setPlatformFilter(v); setSystemFilter("all"); setCurrentPage(1); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Platforms</SelectItem>
                  {platforms.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* System Filter */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">System</p>
              <Select value={systemFilter} onValueChange={(v) => { setSystemFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10">
                  <SelectValue placeholder="All Systems" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                  <SelectItem value="all">All Systems</SelectItem>
                  {(platformFilter !== "all" 
                    ? (systemOptions[platformFilter] || [])
                    : getAllSystems()
                  ).map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Release Year Filter */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Release Year</p>
              <Select value={releaseYearFilter} onValueChange={(v) => { setReleaseYearFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                  <SelectItem value="all">All Years</SelectItem>
                  {getYearOptions().map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Region Filter */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Region</p>
              <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="mdb-region-filter">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Missing Fields Filter */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Missing Fields</p>
              <Select value={missingFilter} onValueChange={(v) => { setMissingFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10">
                  <SelectValue placeholder="No filter" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {missingFieldOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Review Types — multi-select OR filter. Each checkbox toggles
                inclusion of that review category in the games list query. */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Review Tags</p>
              <div className="space-y-1.5" data-testid="review-types-filter">
                {REVIEW_TYPE_OPTIONS.map(({ value, label }) => {
                  const checked = reviewTypesFilter.includes(value);
                  return (
                    <label key={value} className="flex items-center gap-2 text-xs text-white/70 hover:text-white cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => {
                          setReviewTypesFilter(prev => c
                            ? [...prev, value]
                            : prev.filter(v => v !== value));
                          setCurrentPage(1);
                        }}
                        data-testid={`review-types-filter-${value}`}
                      />
                      {label}
                    </label>
                  );
                })}
                {reviewTypesFilter.length > 0 && (
                  <button
                    onClick={() => { setReviewTypesFilter([]); setCurrentPage(1); }}
                    className="text-[10px] text-white/40 hover:text-white/70 mt-1"
                    data-testid="review-types-filter-clear"
                  >
                    Clear review filter
                  </button>
                )}
              </div>
            </div>
            
            {/* Clear Filters */}
            {(platformFilter !== "all" || systemFilter !== "all" || releaseYearFilter !== "all" || regionFilter !== "all" || missingFilter !== "all" || reviewTypesFilter.length > 0) && (
              <div className="p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPlatformFilter("all");
                    setSystemFilter("all");
                    setReleaseYearFilter("all");
                    setRegionFilter("all");
                    setMissingFilter("all");
                    setReviewTypesFilter([]);
                    setCurrentPage(1);
                  }}
                  className="w-full text-xs text-white/50 hover:text-white"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        
        {/* Sort Controls */}
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
          <SelectTrigger className="w-40 bg-black/50 border-white/10" data-testid="master-db-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            {sortOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setSortOrder(sortOrder === "asc" ? "desc" : "asc"); setCurrentPage(1); }}
          className="bg-black/50 border-white/10 text-white/70 hover:text-white px-3"
          data-testid="master-db-sort-order"
        >
          {sortOrder === "desc" ? "Z → A" : "A → Z"}
        </Button>
      </div>

      {/* Main Content - Three Column Layout on Desktop, Stacked on Mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[400px] lg:h-[calc(100vh-280px)]">
        {/* Column 1: Games List */}
        <Card className="bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden h-[500px] sm:h-[550px] lg:h-auto">
          <CardHeader className="border-b border-white/10 flex-shrink-0 py-2 sm:py-3">
            <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
              <Gamepad2 className="w-4 h-4" />
              Games ({totalGames})
              <div className="ml-auto flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs border-cyan-500/30 text-cyan-300 hover:text-white hover:bg-cyan-500/10"
                  onClick={() => setAutoLinkOpen(true)}
                  data-testid="mdb-auto-link-versions"
                  title="Scan for same-named games and link them as versions"
                >
                  <Layers className="w-3.5 h-3.5 mr-1" />
                  Auto-Link
                </Button>
                <Button
                  variant={bulkMode ? "default" : "outline"}
                  size="sm"
                  className={`h-7 px-2 text-xs ${bulkMode ? "bg-purple-500 hover:bg-purple-600 text-white" : "border-white/10 text-white/60 hover:text-white"}`}
                  onClick={toggleBulkMode}
                  data-testid="mdb-bulk-mode-toggle"
                  title="Multi-select mode"
                >
                  {bulkMode ? "Done" : "Select"}
                </Button>
              </div>
            </CardTitle>
            {bulkMode && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="mdb-bulk-toolbar">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="text-xs text-white/70 hover:text-white underline decoration-dotted underline-offset-2 disabled:no-underline disabled:text-white/40"
                      disabled={bulkSelectedIds.size === 0}
                      data-testid="mdb-bulk-view-selected"
                      title="View all selected games across pages"
                    >
                      {bulkSelectedIds.size} selected
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 bg-[#0A0A0A] border-white/10 p-0" align="start">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                      <span className="text-xs font-medium text-white">Selected games ({bulkSelectedIds.size})</span>
                      {bulkSelectedIds.size > 0 && (
                        <button
                          className="text-[10px] text-red-400/80 hover:text-red-300"
                          onClick={clearBulkSelection}
                          data-testid="mdb-bulk-tray-clear"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1" data-testid="mdb-bulk-tray-list">
                      {getSelectedGameObjects().length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-white/40">No games selected yet</div>
                      ) : (
                        getSelectedGameObjects().map((g) => (
                          <div
                            key={g.id}
                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 group"
                            data-testid={`mdb-bulk-tray-item-${g.id}`}
                          >
                            {g.boxArtUrl ? (
                              <img src={g.boxArtUrl} alt="" className="w-6 h-8 object-cover rounded-sm flex-shrink-0 bg-white/5" />
                            ) : (
                              <div className="w-6 h-8 rounded-sm flex-shrink-0 bg-white/10 flex items-center justify-center">
                                <Gamepad2 className="w-3 h-3 text-white/30" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-white truncate">{g.name}</p>
                              {(g.systems?.length > 0 || g.releaseDate) && (
                                <p className="text-[10px] text-white/40 truncate">
                                  {[g.primarySystem || g.systems?.[0], g.releaseDate].filter(Boolean).join(" · ")}
                                </p>
                              )}
                            </div>
                            <button
                              className="text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                              onClick={() => removeFromSelection(g.id)}
                              title="Remove from selection"
                              data-testid={`mdb-bulk-tray-remove-${g.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-white/50 hover:text-white"
                  onClick={selectAllVisibleBulk} data-testid="mdb-bulk-select-all">
                  Select all visible
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-white/50 hover:text-white"
                  onClick={clearBulkSelection} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-clear">
                  Clear
                </Button>
                <div className="w-full flex flex-wrap gap-1.5 mt-1">
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-amber-500 hover:bg-amber-600 text-black"
                    onClick={() => openReviewTypesDialog()} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-review-types">
                    Set Review Types
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={() => openPlatformsDialog()} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-platforms">
                    Set Platforms
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-cyan-500 hover:bg-cyan-600 text-black"
                    onClick={() => openSystemsDialog()} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-systems">
                    Set Systems
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-purple-500 hover:bg-purple-600 text-white"
                    onClick={() => {
                      if (bulkSelectedIds.size === 0) { toast.error("Select at least one game first"); return; }
                      setBulkEditDialogOpen(true);
                    }}
                    disabled={bulkSelectedIds.size === 0}
                    data-testid="mdb-bulk-edit">
                    Bulk Edit…
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-green-500 hover:bg-green-600 text-black"
                    onClick={() => handleBulkPublishSelected(true)} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-publish">
                    Publish
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-white/20 text-white/70 hover:bg-white/5"
                    onClick={() => handleBulkPublishSelected(false)} disabled={bulkSelectedIds.size === 0} data-testid="mdb-bulk-unpublish">
                    Unpublish
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={handleBulkLinkVersions} disabled={bulkSelectedIds.size < 2} data-testid="mdb-bulk-link-versions"
                    title="Link selected games as versions of the same game">
                    Link as Versions
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    onClick={handleBulkLinkCrossPlay} disabled={bulkSelectedIds.size < 2} data-testid="mdb-bulk-link-crossplay"
                    title="Cross-play link selected games for multiplayer">
                    Link Cross-Play
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[10px] bg-fuchsia-500/80 hover:bg-fuchsia-600 text-white"
                    onClick={() => { if (bulkSelectedIds.size < 2) { toast.error("Select at least 2 games to relate"); return; } setBulkRelateOpen(true); }}
                    disabled={bulkSelectedIds.size < 2} data-testid="mdb-bulk-link-related"
                    title="Relate selected games (remaster, remake, alternate, collection)">
                    Link Related…
                  </Button>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden flex flex-col">
            {loading ? (
              <div className="flex justify-center py-8 sm:py-12">
                <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-white/50" />
              </div>
            ) : games.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-white/50 px-4">
                <Database className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-sm sm:text-base">No games in master database</p>
                <p className="text-xs sm:text-sm mt-1">Import from your library or add manually</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5 overflow-y-auto flex-1">
                {games.map((game) => {
                  const isBulkSelected = bulkSelectedIds.has(game.id);
                  return (
                  <div
                    key={game.id}
                    onClick={() => bulkMode ? toggleBulkSelect(game.id) : fetchGameDetails(game.id)}
                    className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-4 cursor-pointer hover:bg-white/5 transition-colors ${
                      bulkMode && isBulkSelected
                        ? "bg-purple-500/15 border-l-2 border-purple-400"
                        : selectedGame?.id === game.id && !bulkMode
                          ? "bg-purple-500/10 border-l-2 border-purple-500"
                          : ""
                    }`}
                    data-testid={`master-game-${game.id}`}
                  >
                    {bulkMode && (
                      <Checkbox
                        checked={isBulkSelected}
                        onCheckedChange={() => toggleBulkSelect(game.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0"
                        data-testid={`mdb-bulk-checkbox-${game.id}`}
                      />
                    )}
                    <div className="w-10 sm:w-12 flex-shrink-0">
                      <div className="flex flex-col rounded bg-white/10 overflow-hidden">
                        {game.showSystemBar && game.boxArtUrl && (
                          <SystemBarOverlay 
                            platform={game.platforms?.[0]} 
                            system={game.systems?.[0]}
                            override={game.systemBarOverride}
                            show={true}
                          />
                        )}
                        <img
                          src={game.boxArtUrl || "/placeholder-game.png"}
                          alt={game.name}
                          className="w-full h-16 object-contain"
                          onError={(e) => e.target.src = "/placeholder-game.png"}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white truncate">{game.name}</p>
                        {game.isPublished ? (
                          <Eye className="w-3.5 h-3.5 text-green-400 shrink-0" title="Published" />
                        ) : (
                          <EyeOff className="w-3.5 h-3.5 text-white/30 shrink-0" title="Not published" />
                        )}
                        {game.needsReview && (() => {
                          const types = Array.isArray(game.reviewTypes) ? game.reviewTypes : [];
                          const LABELS = { tags: "Achievement Tags", secrets: "Secrets", dlc: "DLC", info: "Information", other: "Other" };
                          const tooltip = types.length === 0
                            ? "Marked for review"
                            : `Needs review: ${types.map(t => LABELS[t] || t).join(", ")}`;
                          const count = types.length;
                          return (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0 inline-flex items-center gap-1"
                              title={tooltip}
                              data-testid="review-badge"
                            >
                              REVIEW
                              {count > 1 && (
                                <span className="text-amber-200/80 text-[9px] font-medium" aria-label={`${count} review types`}>·{count}</span>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <span>{game.totalAchievements} achievements</span>
                        <span>•</span>
                        <span>{game.totalScore} GS</span>
                        {game.releaseDate && (
                          <>
                            <span>•</span>
                            <span>{game.releaseDate}</span>
                          </>
                        )}
                      </div>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {/* Unique platforms with colors */}
                        {[...new Set((game.platforms || []).map(p => p.toLowerCase()))].map((p) => (
                          <span 
                            key={`platform-${p}`} 
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${
                              p.includes('xbox') 
                                ? 'bg-green-500/20 text-green-400'
                                : p.includes('playstation') || p === 'ps4' || p === 'ps5' || p === 'ps3'
                                ? 'bg-blue-500/20 text-blue-400'
                                : p.includes('steam') || p === 'pc'
                                ? 'bg-gray-500/20 text-gray-400'
                                : p.includes('nintendo') || p === 'switch'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-white/10 text-white/60'
                            }`}
                          >
                            {p}
                          </span>
                        ))}
                        {/* Systems */}
                        {game.systems?.map((s) => (
                          <span key={`system-${s}`} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/50">
                            {s}
                          </span>
                        ))}
                        {game.region && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                            {game.region}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </div>
                  );
                })}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-white/10">
                <span className="text-sm text-white/50">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="text-white/50 hover:text-white"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="text-white/50 hover:text-white"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="text-white/50 hover:text-white"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="text-white/50 hover:text-white"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Column 2: Game Info & DLCs */}
        <Card className="bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden">
          <CardHeader className="border-b border-white/10 flex-shrink-0 py-3">
            <div className="flex flex-col gap-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Database className="w-4 h-4 flex-shrink-0" />
                {selectedGame ? selectedGame.name : "Game Info"}
              </CardTitle>
              {selectedGame && (
                <div className="flex gap-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={openUploadImagesDialog}
                    disabled={uploadingImages}
                    className="text-purple-400 hover:text-purple-300 h-7 px-2 text-xs"
                    title="Upload images to Cloudinary"
                    data-testid="upload-images-btn"
                  >
                    {uploadingImages ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3 mr-1" />
                    )}
                    Upload Images
                  </Button>
                  {selectedGame.xboxTitleId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleDboxPreview}
                      disabled={dboxPreviewLoading}
                      className="text-green-400 hover:text-green-300 h-7 px-2 text-xs"
                      title="Update game info from DBox"
                      data-testid="dbox-update-btn"
                    >
                      {dboxPreviewLoading ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3 mr-1" />
                      )}
                      DBox Update
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={openCoverDialog}
                    className="text-orange-400 hover:text-orange-300 h-7 px-2 text-xs"
                    title="Search IGDB & SteamGridDB for cover art"
                    data-testid="get-cover-art-btn"
                  >
                    <ImageIcon className="w-3 h-3 mr-1" />
                    Get Cover Art
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopyGame(selectedGame.id, selectedGame.name)}
                    className="text-blue-400 hover:text-blue-300 h-7 px-2 text-xs"
                    title="Copy game"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="edit-game-btn"
                    onClick={() => {
                      setEditingGame({
                        ...selectedGame,
                        genres: selectedGame.genres || [],
                        contentTags: selectedGame.contentTags || [],
                      });
                      setEditGameDialogOpen(true);
                    }}
                    className="text-white/50 hover:text-white h-7 px-2 text-xs"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteGame(selectedGame.id)}
                    className="text-red-400 hover:text-red-300 h-7 px-2 text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden flex flex-col">
            {!selectedGame ? (
              <div className="text-center py-12 text-white/50">
                <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a game to view achievements</p>
              </div>
            ) : loadingAchievements ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-white/50" />
              </div>
            ) : (
              <div className="flex flex-col flex-1 overflow-y-auto">
                {/* Game Info */}
                <div className="p-4 bg-white/5 border-b border-white/10 flex-shrink-0">
                  <div className="flex gap-4">
                    <div className="w-20 flex-shrink-0">
                      <div className="flex flex-col rounded bg-white/10 overflow-hidden">
                        {selectedGame.showSystemBar && selectedGame.boxArtUrl && (
                          <SystemBarOverlay 
                            platform={selectedGame.platforms?.[0]} 
                            system={selectedGame.systems?.[0]}
                            override={selectedGame.systemBarOverride}
                            show={true}
                          />
                        )}
                        <img
                          src={selectedGame.boxArtUrl || "/placeholder-game.png"}
                          alt={selectedGame.name}
                          className="w-full h-28 object-contain"
                          onError={(e) => e.target.src = "/placeholder-game.png"}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white/60">
                        {selectedGame.developers?.length > 0 ? selectedGame.developers.join(", ") : selectedGame.developer}
                        {(selectedGame.developers?.length > 0 || selectedGame.developer) && (selectedGame.publishers?.length > 0 || selectedGame.publisher) ? " • " : ""}
                        {selectedGame.publishers?.length > 0 ? selectedGame.publishers.join(", ") : selectedGame.publisher}
                      </p>
                      {selectedGame.releaseDate && (
                        <p className="text-sm text-white/60">Released: {selectedGame.releaseDate}</p>
                      )}
                      {(selectedGame.completionEstimateFast || selectedGame.completionEstimateLong || selectedGame.completionEstimate) && (
                        <p className="text-sm text-white/60">
                          <span className="text-white/30">Est. Completion:</span>{" "}
                          {selectedGame.completionEstimateFast && selectedGame.completionEstimateLong
                            ? `${selectedGame.completionEstimateFast} - ${selectedGame.completionEstimateLong}`
                            : selectedGame.completionEstimateFast || selectedGame.completionEstimateLong || selectedGame.completionEstimate}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-lg font-bold text-white">{selectedGame.totalAchievements}</span>
                        <span className="text-white/50">achievements</span>
                        <span className="text-lg font-bold text-yellow-400">{selectedGame.totalScore}</span>
                        <span className="text-white/50">GS</span>
                        {selectedGame.dlcs?.length > 0 && (
                          <>
                            <span className="text-lg font-bold text-blue-400">{selectedGame.dlcs.length}</span>
                            <span className="text-white/50">DLCs</span>
                          </>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedGame.region && (
                          <span className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400">
                            {selectedGame.region}
                          </span>
                        )}
                        {selectedGame.availableDigital && (
                          <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                            Digital
                          </span>
                        )}
                        {selectedGame.availablePhysical && (
                          <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">
                            Physical
                          </span>
                        )}
                      </div>
                      {selectedGame.systems?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedGame.systems.map(sys => (
                            <span key={sys} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                              {getSystemDisplayName(sys)}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Content Tags - Using Unified Tags */}
                      {selectedGame.contentTags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedGame.contentTags.map(tagId => {
                            const tag = unifiedTags.find(t => t.id === tagId) || contentTags.find(t => t.id === tagId);
                            return tag ? (
                              <span 
                                key={tagId} 
                                className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"
                                style={{ backgroundColor: tag.color + "30", color: tag.color }}
                              >
                                <Tag className="w-2.5 h-2.5" />
                                {tag.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                      {/* Genres */}
                      {selectedGame.genres?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedGame.genres.map(genreSlug => {
                            const genre = genres.find(g => g.slug === genreSlug);
                            return (
                              <span 
                                key={genreSlug} 
                                className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"
                                style={{ 
                                  backgroundColor: genre ? `${genre.color}20` : 'rgba(168, 85, 247, 0.2)',
                                  color: genre?.color || '#A855F7'
                                }}
                              >
                                <div 
                                  className="w-1.5 h-1.5 rounded-full" 
                                  style={{ backgroundColor: genre?.color || '#A855F7' }}
                                />
                                {getGenreDisplayName(genreSlug)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* DLCs Section */}
                <div className="p-4 border-b border-white/10 bg-blue-500/5 flex flex-col">
                  <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <h3 className="font-medium text-white flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-400" />
                      DLCs ({selectedGame.dlcs?.length || 0})
                    </h3>
                    {/* Add DLC Button - moved to top */}
                    <CreateDlcDialog
                      open={createDlcDialogOpen}
                      onOpenChange={setCreateDlcDialogOpen}
                      newDlc={newDlc}
                      onNewDlcChange={setNewDlc}
                      onCreate={handleCreateDlc}
                    />
                  </div>
                  {/* Scrollable DLC list */}
                  {selectedGame.dlcs?.length > 0 ? (
                    <MasterDlcList
                      masterGameId={selectedGame.id}
                      dlcs={selectedGame.dlcs}
                      selectedGameAchievements={selectedGameAchievements}
                      onEdit={(dlc) => {
                        setEditingDlc(dlc);
                        setEditDlcDialogOpen(true);
                      }}
                      onDelete={handleDeleteDlc}
                      onCoverSearch={openDlcCoverDialog}
                      onReorderPersisted={() => fetchGameDetails(selectedGame.id)}
                    />
                  ) : (
                    <p className="text-sm text-white/40 text-center py-4">No DLCs added yet</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Column 3: Achievements */}
        <Card className="bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden h-[500px] sm:h-[550px] lg:h-auto">
          <CardHeader className="border-b border-white/10 flex-shrink-0 py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Trophy className="w-4 h-4" />
                Achievements ({selectedGameAchievements.length})
              </CardTitle>
              {selectedGame && (
                <div className="flex items-center gap-1 flex-wrap">
                  {/* Bulk Select Mode Toggle & Actions */}
                  {achBulkSelectMode ? (
                    <>
                      <span className="text-[10px] text-white/60 mr-1">
                        {selectedAchievementIds.length}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={selectedAchievementIds.length === selectedGameAchievements.length ? deselectAllAchievements : selectAllAchievements}
                        className="border-white/20 hover:bg-white/10 h-6 text-[10px] px-2"
                      >
                        {selectedAchievementIds.length === selectedGameAchievements.length ? "None" : "All"}
                      </Button>
                      <Dialog open={bulkMoveDialogOpen} onOpenChange={setBulkMoveDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            disabled={selectedAchievementIds.length === 0}
                            className="bg-blue-600 hover:bg-blue-500 h-6 text-[10px] px-2"
                          >
                            <MoveRight className="w-3 h-3 mr-1" />
                            Move
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm">
                          <DialogHeader>
                            <DialogTitle>Move {selectedAchievementIds.length} Achievement{selectedAchievementIds.length !== 1 ? 's' : ''}</DialogTitle>
                            <DialogDescription className="text-white/60">
                              Select destination for the selected achievements.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label>Move to:</Label>
                              <Select value={bulkMoveTarget} onValueChange={setBulkMoveTarget}>
                                <SelectTrigger className="bg-black/50 border-white/10">
                                  <SelectValue placeholder="Select destination" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/10">
                                  <SelectItem value="none">Base Game</SelectItem>
                                  {selectedGame?.dlcs?.map((dlc) => (
                                    <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                onClick={() => setBulkMoveDialogOpen(false)}
                                className="flex-1 border-white/20"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={handleBulkMoveAchievements}
                                className="flex-1 bg-blue-600 hover:bg-blue-500"
                              >
                                Move
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      {/* Edit Button */}
                      <Button
                        size="sm"
                        disabled={selectedAchievementIds.length === 0}
                        onClick={startBulkEditAchievements}
                        className="bg-purple-600 hover:bg-purple-500 h-6 text-[10px] px-2"
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      {/* Bulk Tags Button */}
                      <Button
                        size="sm"
                        disabled={selectedAchievementIds.length === 0}
                        onClick={() => setShowBulkTagDialog(true)}
                        className="bg-teal-600 hover:bg-teal-500 h-6 text-[10px] px-2"
                        data-testid="bulk-tag-achievements-btn"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        Tags
                      </Button>
                      {/* Delete Button */}
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={selectedAchievementIds.length === 0}
                        onClick={handleBulkDeleteAchievements}
                        className="bg-red-600 hover:bg-red-500 h-6 text-[10px] px-2"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setAchBulkSelectMode(false);
                          setSelectedAchievementIds([]);
                        }}
                        className="text-white/50 hover:text-white h-6 text-[10px] px-2"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Select Button */}
                      {selectedGameAchievements.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAchBulkSelectMode(true)}
                          className="border-white/20 hover:bg-white/10 h-6 text-[10px] px-2"
                          data-testid="bulk-select-ach-btn"
                        >
                          Select
                        </Button>
                      )}
                      {/* Copy Tags from Another Game Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCopyTagsDialogOpen(true)}
                        className="border-cyan-400/30 hover:bg-cyan-400/10 text-cyan-400/70 h-6 text-[10px] px-2"
                        data-testid="copy-tags-btn"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy Tags From...
                      </Button>
                      {/* Import from Exophase Button */}
                      <Dialog open={exophaseDialogOpen} onOpenChange={(open) => {
                        setExophaseDialogOpen(open);
                        if (!open) {
                          setExophaseUrl("");
                          setExophaseHtml("");
                          setExophaseAchievements([]);
                          setExophaseImportProgress(0);
                          setExophaseInputMode("url");
                          setExophaseImagesOnly(false);
                          setExophaseApplyScores(false);
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-white/20 hover:bg-white/10 h-6 text-[10px] px-2" data-testid="import-exophase-btn">
                            <Download className="w-3 h-3" />
                          </Button>
                        </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                      <DialogHeader>
                        <DialogTitle>Import from Exophase</DialogTitle>
                        <DialogDescription className="text-white/60">
                          Import achievements from an Exophase game page. Images will be uploaded to Cloudinary.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4 flex-1 overflow-y-auto">
                        {/* Input Mode Toggle */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={exophaseInputMode === "url" ? "default" : "outline"}
                            onClick={() => setExophaseInputMode("url")}
                            className={exophaseInputMode === "url" ? "bg-purple-600" : "border-white/20"}
                            disabled={exophaseLoading || exophaseImporting}
                          >
                            URL
                          </Button>
                          <Button
                            size="sm"
                            variant={exophaseInputMode === "html" ? "default" : "outline"}
                            onClick={() => setExophaseInputMode("html")}
                            className={exophaseInputMode === "html" ? "bg-purple-600" : "border-white/20"}
                            disabled={exophaseLoading || exophaseImporting}
                          >
                            Paste HTML
                          </Button>
                        </div>
                        
                        {/* URL Input */}
                        {exophaseInputMode === "url" && (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                value={exophaseUrl}
                                onChange={(e) => setExophaseUrl(e.target.value)}
                                placeholder="https://www.exophase.com/game/GAME-NAME/achievements/"
                                className="bg-black/50 border-white/10 flex-1"
                                disabled={exophaseLoading || exophaseImporting}
                              />
                              <Button 
                                onClick={handleExophaseScrape}
                                disabled={exophaseLoading || exophaseImporting || !exophaseUrl.trim()}
                                className="bg-purple-600 hover:bg-purple-500"
                              >
                                {exophaseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fetch"}
                              </Button>
                            </div>
                            <p className="text-xs text-white/40">
                              Note: If URL fetching is blocked, use the &quot;Paste HTML&quot; option.
                            </p>
                          </div>
                        )}
                        
                        {/* HTML Paste Input */}
                        {exophaseInputMode === "html" && (
                          <div className="space-y-2">
                            <Textarea
                              value={exophaseHtml}
                              onChange={(e) => setExophaseHtml(e.target.value)}
                              placeholder="Right-click the Exophase page → View Page Source → Copy all → Paste here"
                              className="bg-black/50 border-white/10 min-h-[120px] font-mono text-xs"
                              disabled={exophaseLoading || exophaseImporting}
                            />
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-white/40">
                                Paste the full HTML source from the Exophase achievements page.
                              </p>
                              <Button 
                                onClick={handleExophaseScrape}
                                disabled={exophaseLoading || exophaseImporting || !exophaseHtml.trim()}
                                className="bg-purple-600 hover:bg-purple-500"
                              >
                                {exophaseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Parse"}
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Results Preview */}
                        {exophaseAchievements.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-white/70">
                                Found {exophaseAchievements.length} achievements
                              </p>
                              {exophaseImporting && (
                                <div className="flex items-center gap-2">
                                  <Progress value={exophaseImportProgress} className="w-32 h-2" />
                                  <span className="text-xs text-white/60">{exophaseImportProgress}%</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Achievement Preview List */}
                            <div className="border border-white/10 rounded-lg max-h-[300px] overflow-y-auto">
                              {exophaseAchievements.map((ach, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 border-b border-white/5 last:border-b-0">
                                  {ach.imageUrl && (
                                    <img 
                                      src={ach.imageUrl} 
                                      alt="" 
                                      className="w-10 h-10 rounded object-contain bg-black/50"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{ach.name}</p>
                                    <p className="text-xs text-white/50 truncate">{ach.description}</p>
                                  </div>
                                  <div className="text-xs text-purple-400 font-medium">
                                    {ach.score}G
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Import Button */}
                            
                            {/* Import Mode Selector — 3 ways to handle existing achievements */}
                            <div className="p-3 bg-white/[0.03] border border-white/10 rounded-lg space-y-2" data-testid="exophase-import-mode">
                              <Label className="text-xs text-white/60 uppercase tracking-wide">Import Mode</Label>
                              <div className="grid grid-cols-3 gap-1.5">
                                <button
                                  type="button"
                                  data-testid="exophase-mode-all"
                                  disabled={exophaseImporting}
                                  onClick={() => { setExophaseImagesOnly(false); setExophaseAddNewOnly(false); }}
                                  className={`text-left p-2 rounded border text-xs transition-colors ${
                                    !exophaseImagesOnly && !exophaseAddNewOnly
                                      ? 'bg-green-500/15 border-green-500/40 text-green-300'
                                      : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-white/30'
                                  }`}
                                >
                                  <div className="font-medium">Import All</div>
                                  <div className="text-[10px] mt-0.5 opacity-70 leading-tight">Creates every achievement (may duplicate existing ones)</div>
                                </button>
                                <button
                                  type="button"
                                  data-testid="exophase-mode-new-only"
                                  disabled={exophaseImporting}
                                  onClick={() => { setExophaseImagesOnly(false); setExophaseAddNewOnly(true); }}
                                  className={`text-left p-2 rounded border text-xs transition-colors ${
                                    exophaseAddNewOnly
                                      ? 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                                      : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-white/30'
                                  }`}
                                >
                                  <div className="font-medium">Add New Only</div>
                                  <div className="text-[10px] mt-0.5 opacity-70 leading-tight">Skip achievements that already exist (matches by name)</div>
                                </button>
                                <button
                                  type="button"
                                  data-testid="exophase-mode-images-only"
                                  disabled={exophaseImporting}
                                  onClick={() => { setExophaseImagesOnly(true); setExophaseAddNewOnly(false); }}
                                  className={`text-left p-2 rounded border text-xs transition-colors ${
                                    exophaseImagesOnly
                                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
                                      : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-white/30'
                                  }`}
                                >
                                  <div className="font-medium">Update Images</div>
                                  <div className="text-[10px] mt-0.5 opacity-70 leading-tight">Only refresh images on existing achievements</div>
                                </button>
                              </div>

                              {/* Also update XP / Gamerscore from Exophase
                                  Only meaningful for "Update Images" mode since the other
                                  modes already create achievements with the parsed score. */}
                              {exophaseImagesOnly && (
                                <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="exophase-apply-scores-row">
                                  <Checkbox
                                    checked={!!exophaseApplyScores}
                                    onCheckedChange={(v) => setExophaseApplyScores(!!v)}
                                    disabled={exophaseImporting}
                                    data-testid="exophase-apply-scores-checkbox"
                                  />
                                  <div className="flex-1">
                                    <div className="text-xs text-white/80">
                                      Also update XP / Gamerscore from Exophase
                                    </div>
                                    <p className="text-[10px] text-white/40 mt-0.5">
                                      Replaces each matched achievement&apos;s score with the value
                                      parsed from Exophase. Useful when copying a game between
                                      platforms with different point values (PSN XP ↔ Xbox GS).
                                    </p>
                                  </div>
                                </label>
                              )}
                            </div>
                            
                            <Button
                              onClick={handleExophaseImport}
                              disabled={exophaseImporting}
                              data-testid="exophase-import-action-btn"
                              className={`w-full ${
                                exophaseImagesOnly ? 'bg-amber-600 hover:bg-amber-500'
                                : exophaseAddNewOnly ? 'bg-blue-600 hover:bg-blue-500'
                                : 'bg-green-600 hover:bg-green-500'
                              }`}
                            >
                              {exophaseImporting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  {exophaseImagesOnly ? 'Updating Images...'
                                    : exophaseAddNewOnly ? 'Adding New...'
                                    : 'Importing...'} ({exophaseImportProgress}%)
                                </>
                              ) : exophaseImagesOnly ? (
                                <>
                                  <ImageIcon className="w-4 h-4 mr-2" />
                                  Update Images for {exophaseAchievements.length} Achievements
                                </>
                              ) : exophaseAddNewOnly ? (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Add New Achievements (from {exophaseAchievements.length} parsed)
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-2" />
                                  Import {exophaseAchievements.length} Achievements to Cloudinary
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                        
                        {/* Help Text */}
                        {exophaseAchievements.length === 0 && !exophaseLoading && (
                          <div className="text-center py-8 text-white/40">
                            <ExternalLink className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">
                              Enter an Exophase URL and click "Fetch" to preview achievements.
                            </p>
                            <p className="text-xs mt-2">
                              Images will be downloaded and uploaded to your Cloudinary account.
                            </p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Add Achievement Button */}
                  <CreateAchievementDialog
                    open={createAchievementDialogOpen}
                    onOpenChange={setCreateAchievementDialogOpen}
                    newAchievement={newAchievement}
                    onNewAchievementChange={setNewAchievement}
                    onCreate={handleCreateAchievement}
                    selectedGame={selectedGame}
                  />
                </>
              )}
            </div>
          )}
        </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            {!selectedGame ? (
              <div className="text-center py-12 text-white/50">
                <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a game</p>
              </div>
            ) : loadingAchievements ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-white/50" />
              </div>
            ) : (
              <div className="divide-y divide-white/5 overflow-y-auto h-full">
                {selectedGameAchievements.length === 0 ? (
                  <div className="text-center py-8 text-white/50">
                    <p>No achievements yet</p>
                  </div>
                ) : (
                  selectedGameAchievements.map((ach) => (
                    <div 
                      key={ach.id} 
                      className={`flex items-center gap-2 p-2 hover:bg-white/5 ${achBulkSelectMode && selectedAchievementIds.includes(ach.id) ? 'bg-blue-500/10' : ''}`}
                      data-testid={`master-ach-${ach.id}`}
                      onClick={achBulkSelectMode ? () => toggleAchievementSelection(ach.id) : undefined}
                      style={achBulkSelectMode ? { cursor: 'pointer' } : undefined}
                    >
                      {achBulkSelectMode && (
                        <Checkbox
                          checked={selectedAchievementIds.includes(ach.id)}
                          onCheckedChange={() => toggleAchievementSelection(ach.id)}
                          className="flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <AchievementImage
                        src={ach.imageUrl || "/placeholder-achievement.png"}
                        additionalImages={ach.additionalImages}
                        alt={ach.name}
                        className="w-8 h-8 rounded object-contain bg-black/50 flex-shrink-0"
                        onError={(e) => e.target.src = "/placeholder-achievement.png"}
                      />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="font-medium text-white text-xs truncate">{ach.name}</p>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {ach.score > 0 && <span className="text-[9px] px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-400">{ach.score}</span>}
                          {ach.trophyType && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 capitalize">{ach.trophyType}</span>}
                          {ach.dlcName && <span className="text-[9px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-400 truncate max-w-[60px]">{ach.dlcName}</span>}
                          {/* Achievement Tags - sorted alphabetically */}
                          {ach.tags?.slice()
                            .map(tagId => (predefinedTags || []).find(t => t.id === tagId))
                            .filter(Boolean)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .slice(0, 3)
                            .map(tag => (
                              <span 
                                key={tag.id}
                                className="text-[9px] px-1 py-0.5 rounded truncate max-w-[60px]"
                                style={{ backgroundColor: tag.color + "30", color: tag.color }}
                                title={tag.name}
                              >
                                {tag.name}
                              </span>
                            ))}
                          {ach.tags?.length > 3 && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-white/10 text-white/60">
                              +{ach.tags.length - 3}
                            </span>
                          )}
                          {pendingFactsForGame[ach.id] && <PendingFactBadge count={pendingFactsForGame[ach.id].count} />}
                        </div>
                      </div>
                      {!achBulkSelectMode && (
                        <div className="flex gap-0.5 flex-shrink-0 ml-auto">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setFactsAchievement(ach);
                              setFactsDialogOpen(true);
                            }}
                            className="text-amber-400/60 hover:text-amber-400 h-6 w-6 p-0"
                            title="Manage facts"
                          >
                            <Lightbulb className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingAchievement(ach);
                              setEditAchievementDialogOpen(true);
                            }}
                            className="text-white/50 hover:text-white h-6 w-6 p-0"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAchievement(ach.id)}
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Game Dialog */}
      <EditGameDialog
        open={editGameDialogOpen}
        onOpenChange={setEditGameDialogOpen}
        editingGame={editingGame}
        onEditingGameChange={setEditingGame}
        onSave={handleUpdateGame}
        platforms={platforms}
        genres={genres}
        unifiedTags={unifiedTags}
        unifiedTagGroups={unifiedTagGroups}
        contentTags={contentTags}
        systemOptions={systemOptions}
        selectedGame={selectedGame}
        onXboxLookup={handleXboxLookup}
        xboxLookupLoading={xboxLookupLoading}
        onSteamLookup={handleSteamLookup}
        steamLookupLoading={steamLookupLoading}
        onPsnLookup={handlePsnLookup}
        psnLookupLoading={psnLookupLoading}
        onRemapGenre={handleRemapGenre}
        regionOptions={regionOptions}
      />

      {/* Edit Achievement Dialog */}
      <EditAchievementDialog
        open={editAchievementDialogOpen}
        onOpenChange={setEditAchievementDialogOpen}
        editingAchievement={editingAchievement}
        onEditingAchievementChange={setEditingAchievement}
        onSave={handleUpdateAchievement}
        selectedGame={selectedGame}
        predefinedTags={predefinedTags}
        tagGroups={achievementTagGroups}
      />

      {/* Edit DLC Dialog */}
      <EditDlcDialog
        open={editDlcDialogOpen}
        onOpenChange={setEditDlcDialogOpen}
        editingDlc={editingDlc}
        onEditingDlcChange={setEditingDlc}
        onSave={handleUpdateDlc}
        regionOptions={regionOptions}
      />

      {/* Xbox Title ID Lookup Results Dialog */}
      {/* Platform Lookup Dialogs */}
      <XboxLookupDialog
        open={xboxLookupDialogOpen}
        onOpenChange={setXboxLookupDialogOpen}
        results={xboxLookupResults}
        onSelectTitle={selectXboxTitle}
      />

      <SteamLookupDialog
        open={steamLookupDialogOpen}
        onOpenChange={setSteamLookupDialogOpen}
        results={steamLookupResults}
        onSelectGame={selectSteamGame}
      />

      <PSNLookupDialog
        open={psnLookupDialogOpen}
        onOpenChange={setPsnLookupDialogOpen}
        results={psnLookupResults}
        onSelectGame={selectPsnGame}
      />

      {/* Bulk Edit Achievement Dialog */}
      <BulkEditAchievementDialog
        open={bulkEditAchDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setBulkEditAchDialogOpen(false);
            setBulkEditAchQueue([]);
            setBulkEditAchCurrent(null);
            setBulkEditAchIndex(0);
          }
        }}
        currentAchievement={bulkEditAchCurrent}
        onCurrentAchievementChange={setBulkEditAchCurrent}
        currentIndex={bulkEditAchIndex}
        totalCount={bulkEditAchQueue.length}
        selectedGame={selectedGame}
        onSkip={handleBulkEditAchSkip}
        onSave={handleBulkEditAchSave}
      />

      {/* Upload Images Dialog */}
      <Dialog open={uploadImagesDialogOpen} onOpenChange={setUploadImagesDialogOpen}>
        <DialogContent className="sm:max-w-[450px] bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-500" />
              Upload Images to Cloudinary
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Select which images to upload for "{selectedGame?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <Checkbox
                id="upload-box-art"
                checked={uploadOptions.boxArt}
                onCheckedChange={(checked) => setUploadOptions(prev => ({ ...prev, boxArt: checked }))}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="upload-box-art" className="text-white cursor-pointer font-medium">
                  Box Art
                </Label>
                <p className="text-xs text-white/50">
                  Upload the game's cover image
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <Checkbox
                id="upload-dlc-box-art"
                checked={uploadOptions.dlcBoxArt}
                onCheckedChange={(checked) => setUploadOptions(prev => ({ ...prev, dlcBoxArt: checked }))}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="upload-dlc-box-art" className="text-white cursor-pointer font-medium">
                  DLC Box Art
                </Label>
                <p className="text-xs text-white/50">
                  Upload cover images for all DLCs
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <Checkbox
                id="upload-achievements"
                checked={uploadOptions.achievements}
                onCheckedChange={(checked) => setUploadOptions(prev => ({ ...prev, achievements: checked }))}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="upload-achievements" className="text-white cursor-pointer font-medium">
                  Achievement Images
                </Label>
                <p className="text-xs text-white/50">
                  Upload all achievement icons (may take a while for large games)
                </p>
              </div>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mt-4">
              <p className="text-xs text-blue-300">
                Images already stored in Cloudinary will be skipped. Only platform-sourced images (Xbox, PlayStation, Steam) will be uploaded.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setUploadImagesDialogOpen(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadImages}
              disabled={uploadingImages || (!uploadOptions.boxArt && !uploadOptions.dlcBoxArt && !uploadOptions.achievements)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="upload-images-confirm-btn"
            >
              {uploadingImages ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Selected
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* DBox Update Dialog */}
      <DBoxUpdateDialog
        open={dboxUpdateDialogOpen}
        onOpenChange={setDboxUpdateDialogOpen}
        selectedGame={selectedGame}
        previewLoading={dboxPreviewLoading}
        previewData={dboxPreviewData}
        selectedFields={dboxSelectedFields}
        onToggleField={toggleDboxField}
        applyAchievements={dboxApplyAchievements}
        onApplyAchievementsChange={setDboxApplyAchievements}
        uploadToCloudinary={dboxUploadToCloudinary}
        onUploadToCloudinaryChange={setDboxUploadToCloudinary}
        exophaseHtml={dboxExophaseHtml}
        onExophaseHtmlChange={setDboxExophaseHtml}
        applyExophaseScores={dboxApplyExophaseScores}
        onApplyExophaseScoresChange={setDboxApplyExophaseScores}
        customBoxArtUrl={dboxCustomBoxArtUrl}
        onCustomBoxArtUrlChange={setDboxCustomBoxArtUrl}
        updateLoading={dboxUpdateLoading}
        onUpdate={handleDboxUpdate}
      />
      
      {/* Bug Reports Admin Dialog */}
      <BugReportsDialog
        open={bugReportsDialogOpen}
        onOpenChange={setBugReportsDialogOpen}
        bugReports={bugReports}
        bugReportsLoading={bugReportsLoading}
        onUpdateReport={handleUpdateBugReport}
        onDeleteReport={handleDeleteBugReport}
      />
      
      {/* Known Issues Admin Dialog */}
      <KnownIssuesDialog
        open={knownIssuesDialogOpen}
        onOpenChange={setKnownIssuesDialogOpen}
        knownIssues={knownIssues}
        knownIssuesLoading={knownIssuesLoading}
        onCreateIssue={handleCreateKnownIssue}
        onUpdateIssue={handleUpdateKnownIssue}
        onDeleteIssue={handleDeleteKnownIssue}
      />
      
      {/* Platform Diagnostics Dialog */}
      <Dialog open={platformDiagnosticsOpen} onOpenChange={setPlatformDiagnosticsOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-400" />
              Platform Data Diagnostics
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Find and fix inconsistent platform naming (e.g., "xbox" vs "Xbox")
            </DialogDescription>
          </DialogHeader>
          
          {platformDiagnosticsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : platformDiagnostics ? (
            <div className="space-y-6 pt-4">
              {/* All Platforms - Detailed View */}
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">All Platform Values in Database (with details)</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto bg-black/30 rounded p-2">
                  {platformDiagnostics.allPlatformDetails?.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm font-mono">
                      <span className="px-2 py-0.5 bg-white/10 rounded">"{p.value}"</span>
                      <span className="text-white/40">len={p.length}</span>
                      <span className="text-white/40">repr={p.valueRepr}</span>
                      <span className="text-white/60">({p.count} games)</span>
                    </div>
                  )) || platformDiagnostics.allPlatforms?.map((p) => (
                    <span key={p} className="px-2 py-1 bg-white/10 rounded text-sm">{p}</span>
                  ))}
                </div>
              </div>
              
              {/* Inconsistencies */}
              {platformDiagnostics.inconsistencies?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-400 mb-3">⚠️ Duplicate Platform Names Found (different strings, same meaning)</h3>
                  {platformDiagnostics.inconsistencies.map((inc) => (
                    <div key={inc.normalizedKey} className="mb-4 last:mb-0">
                      <p className="text-white/80 mb-2">Multiple variants of "{inc.normalizedKey}":</p>
                      {inc.variants.map((v, idx) => (
                        <div key={idx} className="ml-4 mb-2 bg-black/20 p-2 rounded">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono bg-white/10 px-2 py-1 rounded">"{v.value}"</span>
                            <span className="text-xs text-white/40">len={v.length}</span>
                            <span className="text-xs text-white/40">repr={v.valueRepr}</span>
                            <span className="text-white/50">({v.count} games)</span>
                          </div>
                          {v.sampleGames?.length > 0 && (
                            <div className="text-xs text-white/40 mt-1">
                              Examples: {v.sampleGames.map(g => g.name).join(", ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Non-Canonical Platforms */}
              {platformDiagnostics.nonCanonicalPlatforms?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-amber-400 mb-3">⚠️ Non-Canonical Platform Names (wrong casing or whitespace)</h3>
                  {platformDiagnostics.nonCanonicalPlatforms.map((p, idx) => (
                    <div key={idx} className="mb-3 last:mb-0 bg-black/20 p-2 rounded">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono bg-white/10 px-2 py-1 rounded">"{p.value}"</span>
                        <span className="text-xs text-white/40">len={p.length}</span>
                        <span className="text-xs text-white/40">repr={p.valueRepr}</span>
                        <span className="text-white/50">→</span>
                        <span className="font-mono bg-green-500/20 px-2 py-1 rounded text-green-400">"{p.shouldBe}"</span>
                        <span className="text-white/50">({p.count} games)</span>
                      </div>
                      {p.sampleGames?.length > 0 && (
                        <div className="text-xs text-white/40 mt-1">
                          Examples: {p.sampleGames.map(g => g.name).join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* No Issues */}
              {(!platformDiagnostics.inconsistencies?.length && !platformDiagnostics.nonCanonicalPlatforms?.length) && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-400 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    All platform names are consistent and canonical!
                  </p>
                </div>
              )}
              
              {/* Fix Button */}
              {(platformDiagnostics.inconsistencies?.length > 0 || platformDiagnostics.nonCanonicalPlatforms?.length > 0) && (
                <div className="flex justify-end pt-4 border-t border-white/10">
                  <Button
                    onClick={handleNormalizePlatforms}
                    disabled={normalizingPlatforms}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="normalize-platforms-btn"
                  >
                    {normalizingPlatforms ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Normalizing...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Normalize All Platforms
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-white/50 text-center py-8">Click refresh to load diagnostics</p>
          )}
          
          <div className="flex justify-between pt-4 border-t border-white/10">
            <Button
              variant="outline"
              onClick={fetchPlatformDiagnostics}
              disabled={platformDiagnosticsLoading}
              className="border-white/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${platformDiagnosticsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="ghost" onClick={() => setPlatformDiagnosticsOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Game Cover Art Dialog */}
      <GameCoverArtDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        selectedGame={selectedGame}
        searchQuery={coverSearchQuery}
        onSearchQueryChange={setCoverSearchQuery}
        onSearch={searchCovers}
        loading={coverLoading}
        results={coverResults}
        sourceFilter={coverSourceFilter}
        onSourceFilterChange={setCoverSourceFilter}
        selectedCover={selectedCover}
        onSelectCover={setSelectedCover}
        uploading={coverUploading}
        onApply={applyCover}
        getFilteredResults={getFilteredCoverResults}
      />
      
      {/* DLC Cover Art Dialog */}
      <DlcCoverArtDialog
        open={dlcCoverDialogOpen}
        onOpenChange={setDlcCoverDialogOpen}
        dlcForSearch={dlcForCoverSearch}
        searchQuery={dlcCoverSearchQuery}
        onSearchQueryChange={setDlcCoverSearchQuery}
        onSearch={searchDlcCovers}
        loading={dlcCoverLoading}
        results={dlcCoverResults}
        sourceFilter={dlcCoverSourceFilter}
        onSourceFilterChange={setDlcCoverSourceFilter}
        selectedCover={selectedDlcCover}
        onSelectCover={setSelectedDlcCover}
        uploading={dlcCoverUploading}
        onApply={applyDlcCover}
        getFilteredResults={getFilteredDlcCoverResults}
      />
      
      {/* Genre Remap Dialog */}
      <Dialog open={genreRemapDialogOpen} onOpenChange={setGenreRemapDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Remap Genre</DialogTitle>
            <DialogDescription className="text-white/60">
              Map "{genreToRemap}" to a recognized genre
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Select target genre</Label>
              <Select value={remapTargetGenre} onValueChange={setRemapTargetGenre}>
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Choose a genre..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-60">
                  {[...genres].sort((a, b) => a.name.localeCompare(b.name)).map((genre) => (
                    <SelectItem key={genre.slug} value={genre.slug}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: genre.color }}
                        />
                        {genre.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setGenreRemapDialogOpen(false)}
                className="border-white/20"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmGenreRemap}
                disabled={!remapTargetGenre}
                className="bg-purple-600 hover:bg-purple-500"
              >
                Remap Genre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Management Dialog */}
      <Dialog open={membersDialogOpen} onOpenChange={(open) => {
        setMembersDialogOpen(open);
        if (open) fetchMembers();
      }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Master Database Members
            </DialogTitle>
            <DialogDescription>
              Manage who has access to the Master Database
            </DialogDescription>
          </DialogHeader>
          
          {/* Add New Member - Two modes: email entry or user search */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={!showUserSearch ? "default" : "outline"}
                size="sm"
                onClick={() => setShowUserSearch(false)}
                className={!showUserSearch ? "bg-blue-600" : ""}
              >
                Add by Email
              </Button>
              <Button
                variant={showUserSearch ? "default" : "outline"}
                size="sm"
                onClick={() => setShowUserSearch(true)}
                className={showUserSearch ? "bg-blue-600" : ""}
              >
                <Search className="w-4 h-4 mr-1" />
                Browse Users
              </Button>
            </div>

            {!showUserSearch ? (
              <div className="flex gap-2 p-3 bg-[#121212] rounded-lg border border-white/10">
                <Input
                  value={addMemberEmail}
                  onChange={(e) => setAddMemberEmail(e.target.value)}
                  placeholder="Enter user email..."
                  className="flex-1 bg-black/50 border-white/10"
                  data-testid="add-member-email"
                />
                <Select value={addMemberLevel} onValueChange={setAddMemberLevel}>
                  <SelectTrigger className="w-32 bg-black/50 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAddMember}
                  disabled={addingMember || !addMemberEmail.trim()}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="add-member-btn"
                >
                  {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2 p-3 bg-[#121212] rounded-lg border border-white/10">
                  <Input
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="Search users by email or name..."
                    className="flex-1 bg-black/50 border-white/10"
                    data-testid="user-search-input"
                  />
                  {searchingUsers && <Loader2 className="w-5 h-5 animate-spin text-white/50" />}
                </div>
                
                {/* Search Results */}
                {searchedUsers.length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-1 p-2 bg-[#0a0a0a] rounded-lg border border-white/10">
                    {searchedUsers.map((user) => (
                      <div
                        key={user.user_id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-white/5"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                          {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-4 h-4 text-white/30" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{user.displayName || user.email}</p>
                          <p className="text-xs text-white/40 truncate">{user.email}</p>
                        </div>
                        {user.isAdmin ? (
                          <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">Global Admin</span>
                        ) : user.hasPermission ? (
                          <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                            {user.permissionLevel}
                          </span>
                        ) : (
                          <Select onValueChange={(level) => handleGrantPermission(user, level)}>
                            <SelectTrigger className="w-24 h-7 text-xs bg-blue-600/20 border-blue-500/30 text-blue-400">
                              <span>Grant...</span>
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10">
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="moderator">Moderator</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {userSearchQuery && !searchingUsers && searchedUsers.length === 0 && (
                  <p className="text-sm text-white/40 text-center py-2">No users found</p>
                )}
              </div>
            )}
          </div>
          
          {/* Permission Level Legend */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-gray-500/20 text-gray-400">
              <Eye className="w-3 h-3 inline mr-1" />Viewer: Read only
            </span>
            <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400">
              <Edit2 className="w-3 h-3 inline mr-1" />Editor: Can edit
            </span>
            <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400">
              <Shield className="w-3 h-3 inline mr-1" />Moderator: Edit + approve
            </span>
            <span className="px-2 py-1 rounded bg-amber-500/20 text-amber-400">
              <ShieldCheck className="w-3 h-3 inline mr-1" />Admin: Full access
            </span>
          </div>
          
          {/* Members List */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {membersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/50" />
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-white/40">
                No members yet
              </div>
            ) : (
              members.map((member) => (
                <div
                  key={member.user_id}
                  className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/5"
                  data-testid={`member-${member.user_id}`}
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-white/30" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">
                      {member.displayName || member.email}
                    </p>
                    <p className="text-xs text-white/40 truncate">{member.email}</p>
                  </div>
                  
                  {/* Badge */}
                  {member.isGlobalAdmin && (
                    <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      Global Admin
                    </span>
                  )}
                  
                  {/* Level Selector / Actions */}
                  {!member.isGlobalAdmin && (
                    <>
                      <Select
                        value={member.level}
                        onValueChange={(newLevel) => handleUpdateMemberLevel(member.user_id, newLevel)}
                      >
                        <SelectTrigger className="w-28 bg-black/50 border-white/10 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Remove access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Achievement Facts Manager Dialog */}
      <Dialog open={factsDialogOpen} onOpenChange={setFactsDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          {factsAchievement && selectedGame && (
            <AchievementFactsManager
              achievementId={factsAchievement.id}
              achievementName={factsAchievement.name}
              masterGameId={selectedGame.id}
              gameName={selectedGame.name}
              onClose={() => setFactsDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Tag Manager Dialog */}
      <TagManagerDialog
        open={showTagManager}
        onOpenChange={setShowTagManager}
        unifiedTags={unifiedTags}
        newTagName={newTagName}
        onNewTagNameChange={setNewTagName}
        newTagColor={newTagColor}
        onNewTagColorChange={setNewTagColor}
        newTagDescription={newTagDescription}
        onNewTagDescriptionChange={setNewTagDescription}
        onCreateTag={handleCreateUnifiedTag}
        editingTag={editingTag}
        onEditingTagChange={setEditingTag}
        onUpdateTag={handleUpdateUnifiedTag}
        onDeleteTag={handleDeleteUnifiedTag}
      />

      {/* Per-Achievement Tag Editor Dialog */}
      <BulkAchievementTagsDialog
        open={showBulkTagDialog}
        onOpenChange={setShowBulkTagDialog}
        selectedAchievements={selectedGameAchievements.filter(a => selectedAchievementIds.includes(a.id))}
        predefinedTags={predefinedTags}
        tagGroups={achievementTagGroups}
        onApply={handleBulkTagUpdate}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Achievements</DialogTitle>
            <DialogDescription>
              Delete {selectedAchievementIds.length} achievement{selectedAchievementIds.length !== 1 ? 's' : ''}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)} data-testid="bulk-delete-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={executeBulkDelete} data-testid="bulk-delete-confirm-btn">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Platform Coverage Dialog */}
      <PlatformCoverageDialog open={platformCoverageOpen} onOpenChange={setPlatformCoverageOpen} onAddGame={handleAddGameFromRequest} />
      <CustomPlatformRequestsDialog open={psRequestsOpen} onOpenChange={setPsRequestsOpen} />

      {/* Copy Tags Dialog */}
      <CopyTagsDialog
        open={copyTagsDialogOpen}
        onOpenChange={setCopyTagsDialogOpen}
        targetGameId={selectedGame?.id}
        targetGameName={selectedGame?.name}
        allTags={predefinedTags}
        onApplied={() => {
          // Refresh selected game's achievements
          if (selectedGame?.id) {
            axios.get(`${API}/master/games/${selectedGame.id}/achievements`).then(res => {
              setSelectedGame(prev => ({ ...prev, achievements: res.data }));
            }).catch(() => {});
          }
        }}
      />

      {/* Copy Game dialog — choose a plain copy or a linked version copy */}
      <Dialog open={copyDialog.open} onOpenChange={(open) => !open && setCopyDialog({ open: false, gameId: null, gameName: "" })}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md" data-testid="copy-game-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Copy className="w-5 h-5 text-blue-400" />
              Copy Game
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/60">
              Copying <span className="text-white font-medium">{copyDialog.gameName}</span>. Make this a
              <span className="text-cyan-300 font-medium"> linked version</span> of the original so they collapse to a
              single Browse tile, or create a standalone copy?
            </p>
            <div className="flex flex-col gap-2">
              <Button
                data-testid="copy-game-linked-btn"
                onClick={() => doCopyGame(true)}
                className="w-full bg-cyan-600 hover:bg-cyan-700 justify-start"
              >
                <Layers className="w-4 h-4 mr-2" />
                Make a linked copy (same game, another version)
              </Button>
              <Button
                data-testid="copy-game-plain-btn"
                onClick={() => doCopyGame(false)}
                variant="outline"
                className="w-full bg-white/5 border-white/10 text-white/80 hover:bg-white/10 justify-start"
              >
                <Copy className="w-4 h-4 mr-2" />
                Plain copy (not linked)
              </Button>
              <Button
                variant="ghost"
                onClick={() => setCopyDialog({ open: false, gameId: null, gameName: "" })}
                className="w-full text-white/50 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Link Duplicate Versions tool */}
      <AutoLinkVersionsDialog
        open={autoLinkOpen}
        onOpenChange={setAutoLinkOpen}
        onApplied={() => fetchGames()}
      />

      {/* Bulk relate (remaster/remake/alternate/collection) */}
      <BulkRelateDialog
        open={bulkRelateOpen}
        onOpenChange={setBulkRelateOpen}
        selectedGames={getSelectedGameObjects()}
        onApplied={() => { setBulkSelectedIds(new Set()); }}
      />

      {/* Bulk action confirmation dialog — replaces window.confirm() which
          some browsers / extensions silently dismiss. */}
      <AlertDialog open={!!bulkConfirm} onOpenChange={(open) => !open && !bulkRunning && setBulkConfirm(null)}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{bulkConfirm?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {bulkConfirm?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
              disabled={bulkRunning}
              data-testid="bulk-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); bulkConfirm?.onConfirm?.(); }}
              disabled={bulkRunning}
              className={
                bulkConfirm?.variant === "danger"
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : bulkConfirm?.variant === "warning"
                  ? "bg-amber-500 hover:bg-amber-600 text-black"
                  : bulkConfirm?.variant === "success"
                  ? "bg-green-500 hover:bg-green-600 text-black"
                  : "bg-purple-500 hover:bg-purple-600 text-white"
              }
              data-testid="bulk-confirm-action"
            >
              {bulkRunning ? "Working…" : (bulkConfirm?.confirmLabel || "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* "Set Review Types" dialog — set-exact semantics. Whatever the user
          checks here becomes the new reviewTypes set on all selected games.
          Leaving everything unchecked clears the flag entirely. */}
      <Dialog open={reviewTypesDialogOpen} onOpenChange={(open) => !bulkRunning && setReviewTypesDialogOpen(open)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Set Review Tags</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">
            Pick the kind(s) of review needed on the {bulkSelectedIds.size} selected game{bulkSelectedIds.size === 1 ? "" : "s"}. This <span className="text-amber-300">replaces</span> the current review tags on each game. Leave all unchecked to clear the flag.
          </p>
          <div className="space-y-2 py-2">
            {REVIEW_TYPE_OPTIONS.map(({ value, label }) => {
              const checked = reviewTypesDialogValues.has(value);
              return (
                <label key={value} className="flex items-center gap-3 text-sm text-white/80 hover:text-white cursor-pointer p-2 rounded hover:bg-white/5">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = new Set(reviewTypesDialogValues);
                      c ? next.add(value) : next.delete(value);
                      setReviewTypesDialogValues(next);
                    }}
                    data-testid={`bulk-review-type-${value}`}
                  />
                  {label}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewTypesDialogOpen(false)} disabled={bulkRunning}
              data-testid="bulk-review-cancel">Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-black"
              onClick={applyBulkReviewTypes}
              disabled={bulkRunning}
              data-testid="bulk-review-apply"
            >
              {bulkRunning ? "Working…" : (reviewTypesDialogValues.size === 0 ? "Clear All" : "Apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Set Platforms" bulk dialog — replaces the platforms array on every
          selected game with the exact set the admin picks. */}
      <Dialog open={platformsDialogOpen} onOpenChange={(open) => !bulkRunning && setPlatformsDialogOpen(open)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Set Platforms</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">
            Pick the platform(s) for the {bulkSelectedIds.size} selected game{bulkSelectedIds.size === 1 ? "" : "s"}. This <span className="text-blue-300">replaces</span> the current platforms on each game. Leave all unchecked to clear.
          </p>
          <div className="space-y-1 py-2 max-h-64 overflow-y-auto">
            {platforms.map((p) => {
              const checked = platformsDialogValues.has(p.slug);
              return (
                <label key={p.slug} className="flex items-center gap-3 text-sm text-white/80 hover:text-white cursor-pointer p-2 rounded hover:bg-white/5">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = new Set(platformsDialogValues);
                      c ? next.add(p.slug) : next.delete(p.slug);
                      setPlatformsDialogValues(next);
                    }}
                    data-testid={`bulk-platform-${p.slug}`}
                  />
                  {p.name}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlatformsDialogOpen(false)} disabled={bulkRunning}
              data-testid="bulk-platforms-cancel">Cancel</Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white"
              onClick={applyBulkPlatforms}
              disabled={bulkRunning}
              data-testid="bulk-platforms-apply"
            >
              {bulkRunning ? "Working…" : (platformsDialogValues.size === 0 ? "Clear All" : "Apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Set Systems" bulk dialog — same set-exact semantics. Grouped by
          platform for readability, but a game can inherit systems from any
          platform (the backend replaces `systems` with the whole checked set). */}
      <Dialog open={systemsDialogOpen} onOpenChange={(open) => !bulkRunning && setSystemsDialogOpen(open)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Set Systems</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">
            Pick the system(s) for the {bulkSelectedIds.size} selected game{bulkSelectedIds.size === 1 ? "" : "s"}. This <span className="text-cyan-300">replaces</span> the current systems on each game. If a game&apos;s primary system is no longer in the list, it&apos;ll auto-reset to the first checked system.
          </p>
          <div className="space-y-3 py-2 max-h-80 overflow-y-auto">
            {platforms.map((p) => {
              const opts = systemOptions[p.slug] || [];
              if (opts.length === 0) return null;
              return (
                <div key={p.slug}>
                  <div className="text-[11px] uppercase tracking-wide text-white/40 mb-1">{p.name}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {opts.map((s) => {
                      const checked = systemsDialogValues.has(s.value);
                      return (
                        <label key={s.value} className="flex items-center gap-2 text-sm text-white/80 hover:text-white cursor-pointer p-1.5 rounded hover:bg-white/5">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => {
                              const next = new Set(systemsDialogValues);
                              c ? next.add(s.value) : next.delete(s.value);
                              setSystemsDialogValues(next);
                            }}
                            data-testid={`bulk-system-${s.value}`}
                          />
                          <span className="text-xs">{s.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSystemsDialogOpen(false)} disabled={bulkRunning}
              data-testid="bulk-systems-cancel">Cancel</Button>
            <Button
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
              onClick={applyBulkSystems}
              disabled={bulkRunning}
              data-testid="bulk-systems-apply"
            >
              {bulkRunning ? "Working…" : (systemsDialogValues.size === 0 ? "Clear All" : "Apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mega bulk edit dialog — set any combination of metadata fields
          across selected master games. Uses per-field apply toggles so
          unchecked fields are never sent to the backend. */}
      <BulkEditDialog
        open={bulkEditDialogOpen}
        onOpenChange={setBulkEditDialogOpen}
        selectedIds={bulkSelectedIds}
        genresList={[]}
        onDone={() => { setBulkSelectedIds(new Set()); fetchGames(); }}
      />

    </div>
  );
}
